import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import AdStudioModal from '../components/AdStudioModal';
import VisualPageEditor from '../components/VisualPageEditor';
import PageBuilderAgentModal from '../components/PageBuilderAgentModal';
import PaidTrafficManager from '../components/PaidTrafficManager';
import { auth } from '../firebase';

/* ─── Design tokens ───────────────────────────────────────────────────────── */
const C = {
  bg:'#0d0f14', surface:'#13161d', card:'#1a1d27',
  border:'rgba(255,255,255,0.08)', borderHover:'rgba(255,255,255,0.18)',
  text:'#e8eaf0', muted:'#6b7080',
  orange:'#f97316', green:'#22c55e', red:'#ef4444',
  blue:'#02B6FF', yellow:'#eab308', purple:'#a78bfa', violet:'#8b5cf6',
};

/* ─── Layout constants ────────────────────────────────────────────────────── */
const NW = 220;       // node width
const NH = 186;       // standard node height
const CW = 2800;      // canvas width
const CH = 1600;      // canvas height

// Listicle layout (px from node top)
const L_PAD   = 16;   // padding top
const L_HDR   = 26;   // NodeHeader row + marginBottom
const L_TTL   = 25;   // NodeTitle
const L_STATS = 54;   // StatsGrid w/ marginTop
const L_GAP   = 10;   // gap before first offer row
const L_OFFER = 44;   // each offer row height
const L_BTN   = 38;   // add-offer button area
const L_PRE   = L_PAD + L_HDR + L_TTL + L_STATS + L_GAP; // = 131

// Video Hub total height
const VH_H = 306;

/* ─── Default node data ───────────────────────────────────────────────────── */
const DEFAULT_OFFERS = [
  { id:'o1', name:'SoFi Invest',  epc:'$0.00', conv:'0.0%' },
  { id:'o2', name:'Robinhood',    epc:'$0.00', conv:'0.0%' },
  { id:'o3', name:'Acorns',       epc:'$0.00', conv:'0.0%' },
];
const DEFAULT_LESSONS = [
  { id:'l1', title:'Intro to Affiliate Marketing', dur:'8m 20s', done:true  },
  { id:'l2', title:'Finding Hot Offers',           dur:'12m 14s', active:true },
  { id:'l3', title:'Traffic Secrets',              dur:'9m 45s'  },
  { id:'l4', title:'Scale to $1k/Day',             dur:'15m 02s' },
];

const OFFER_COLORS = [
  { border:'rgba(34,197,94,0.55)',  conn:'rgba(34,197,94,0.6)',  rank:'rgba(34,197,94,0.2)',  rc: C.green  },
  { border:'rgba(59,130,246,0.55)', conn:'rgba(59,130,246,0.6)', rank:'rgba(59,130,246,0.2)', rc: C.blue   },
  { border:'rgba(234,179,8,0.55)',  conn:'rgba(234,179,8,0.6)',  rank:'rgba(234,179,8,0.2)',  rc: C.yellow },
  { border:'rgba(167,139,250,0.55)',conn:'rgba(167,139,250,0.6)',rank:'rgba(167,139,250,0.2)',rc: C.purple },
];

/* ─── Initial state ───────────────────────────────────────────────────────── */
const EMPTY_NODES = [];
const EMPTY_CONNS = [];


/* ─── Node catalog ────────────────────────────────────────────────────────── */
const NODE_CATALOG = [
  { type:'traffic',       label:'📰  Traffic Source',   desc:'Ad network, SEO, social',       border:'rgba(34,197,94,0.4)'  },
  { type:'adv-a',         label:'📄  Advertorial',       desc:'Pre-sell content page',         border:'rgba(239,68,68,0.5)'  },
  { type:'landing',       label:'📋  Landing Page',      desc:'Capture / squeeze page',        border:'rgba(59,130,246,0.35)'},
  { type:'quiz',          label:'❓  Quiz Page',         desc:'Interactive survey/quiz page',  border:'rgba(236,72,153,0.45)' },
  { type:'bridge',        label:'🌉  Bridge Page',       desc:'Product recommendation bridge', border:'rgba(6,182,212,0.45)' },
  { type:'oto',           label:'🔥  OTO Page',          desc:'One-time offer yes/no page',    border:'rgba(244,63,94,0.45)' },
  { type:'offer',         label:'💰  Affiliate Offer',   desc:'Single product or offer',       border:'rgba(234,179,8,0.35)' },
  { type:'listicle',      label:'🗂️  Listicle',           desc:'Multi-offer review page',       border:'rgba(249,115,22,0.45)' },
  { type:'video-hub',     label:'🎬  Video Hub',         desc:'Course / lesson video page',    border:'rgba(139,92,246,0.45)' },
  { type:'capture-popup', label:'💬  Capture Popup',     desc:'Email optin overlay modal',    border:'rgba(167,139,250,0.45)' },
  { type:'custom-html',   label:'💻  Custom HTML Page',  desc:'Paste raw HTML doc (Option A)', border:'rgba(16,185,129,0.5)'  },
  { type:'external-page', label:'🌐  External Page',     desc:'Self-hosted t.js page (Option B)', border:'rgba(245,158,11,0.5)' }
];

/* ─── Dynamic node geometry ───────────────────────────────────────────────── */
const getNodeHeight = (node) => {
  if (node.type === 'listicle') {
    const n = (node.offers || DEFAULT_OFFERS).length;
    return L_PRE + n * L_OFFER + L_BTN + L_PAD;
  }
  if (node.type === 'video-hub') return VH_H;
  return NH;
};

const getNodePorts = (node) => {
  const h = getNodeHeight(node);
  if (node.type === 'listicle') {
    const offers = node.offers || DEFAULT_OFFERS;
    return [
      { id:'left', side:'left', cy: h / 2 },
      ...offers.map((_, i) => ({
        id: `offer-${i}`, side:'right',
        cy: L_PRE + i * L_OFFER + L_OFFER / 2,
      })),
    ];
  }
  if (node.type === 'oto') {
    return [
      { id:'left', side:'left', cy: h / 2 },
      { id:'yes',  side:'right', cy: h * 0.35 },
      { id:'no',   side:'right', cy: h * 0.65 },
    ];
  }
  return [
    { id:'left',  side:'left',  cy: h / 2 },
    { id:'right', side:'right', cy: h / 2 },
  ];
};

const portPos = (nodes, nodeId, portId) => {
  const n = nodes.find(x => x.id === nodeId);
  if (!n) return null;
  const h = getNodeHeight(n);
  if (portId === 'left')  return { x: n.x,      y: n.y + h / 2 };
  if (portId === 'right') return { x: n.x + NW, y: n.y + h / 2 };
  if (portId === 'yes')   return { x: n.x + NW, y: n.y + h * 0.35 };
  if (portId === 'no')    return { x: n.x + NW, y: n.y + h * 0.65 };
  if (portId?.startsWith('offer-')) {
    const i = parseInt(portId.split('-')[1]);
    return { x: n.x + NW, y: n.y + L_PRE + i * L_OFFER + L_OFFER / 2 };
  }
  return { x: n.x + NW, y: n.y + h / 2 };
};

const isOutputPort = (portId) => portId !== 'left';

/* ─── SVG bezier path ─────────────────────────────────────────────────────── */
const makePath = (x1, y1, x2, y2) => {
  const cp = Math.max(Math.abs(x2 - x1) * 0.42, 50);
  return `M ${x1} ${y1} C ${x1+cp} ${y1} ${x2-cp} ${y2} ${x2} ${y2}`;
};

/* ─── Global CSS ──────────────────────────────────────────────────────────── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  html,body,#root{height:100%}
  .cb-root{font-family:'JetBrains Mono','SF Mono','Fira Code',monospace;background:${C.bg};color:${C.text};min-height:100vh;display:flex;flex-direction:column;overflow:hidden}
  .cb-scroll::-webkit-scrollbar{width:5px;height:5px}
  .cb-scroll::-webkit-scrollbar-track{background:transparent}
  .cb-scroll::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.10);border-radius:99px}

  @keyframes bottleneck-pulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.18),0 0 16px rgba(239,68,68,0.08)}50%{box-shadow:0 0 0 8px rgba(239,68,68,0),0 0 36px rgba(239,68,68,0.28)}}
  .bottleneck-pulse{animation:bottleneck-pulse 2s ease-in-out infinite}

  @keyframes live-blink{0%,100%{opacity:1}50%{opacity:.35}}
  .live-dot{animation:live-blink 1.5s ease-in-out infinite}

  @keyframes fill-bar{from{width:0}to{width:58%}}
  .ab-bar-fill{animation:fill-bar 1.2s ease-out .4s both}

  @keyframes msg-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .msg-in{animation:msg-in .35s ease-out forwards}

  @keyframes blink{0%,80%,100%{opacity:0}40%{opacity:1}}
  .typing-dot{animation:blink 1.4s infinite both}
  .typing-dot:nth-child(2){animation-delay:.2s}
  .typing-dot:nth-child(3){animation-delay:.4s}

  @keyframes lesson-glow{0%,100%{box-shadow:0 0 0 0 rgba(139,92,246,0.0)}50%{box-shadow:0 0 12px rgba(139,92,246,0.35)}}
  .lesson-active{animation:lesson-glow 2s ease-in-out infinite}

  .funnel-node{transition:border-color .18s,box-shadow .18s}

  /* port handles */
  .port-handle{position:absolute;width:16px;height:16px;border-radius:50%;border:2px solid rgba(255,255,255,0.18);background:${C.card};cursor:crosshair;z-index:30;transition:border-color .15s,background .15s,box-shadow .15s}
  .port-handle.left{left:-9px}
  .port-handle.right{right:-9px}
  .port-handle:hover,.port-handle.ph-active{background:${C.orange};border-color:${C.orange};box-shadow:0 0 10px ${C.orange}80}
  .port-handle.ph-target:hover{background:${C.green};border-color:${C.green};box-shadow:0 0 10px ${C.green}80}

  /* offer port */
  .port-offer{background:rgba(249,115,22,0.15);border-color:rgba(249,115,22,0.45)}
  .port-offer:hover,.port-offer.ph-active{background:${C.orange};border-color:${C.orange}}

  /* node hover actions */
  .nw-actions{opacity:0;transition:opacity .15s;pointer-events:none}
  .node-wrapper:hover .nw-actions{opacity:1;pointer-events:all}

  /* connection delete */
  .conn-x{opacity:0;transition:opacity .15s}
  .conn-group:hover .conn-x{opacity:1}

  /* lesson row hover */
  .lesson-row{transition:background .12s;cursor:pointer;border-radius:4px;padding:4px 6px}
  .lesson-row:hover{background:rgba(139,92,246,0.08)}

  /* V1.3 Canvas Menu Buttons */
  .canvas-btn {
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 12px;
    padding: 10px 16px;
    color: ${C.text};
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    letter-spacing: 0.04em;
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    transition: all 0.2s ease;
    user-select: none;
    outline: none;
  }
  .canvas-btn:hover {
    border-color: ${C.orange};
    box-shadow: 0 0 12px rgba(249,115,22,0.15);
    transform: translateY(-1px);
    background: ${C.card};
  }
  .canvas-btn.btn-traffic:hover {
    border-color: ${C.green};
    box-shadow: 0 0 12px rgba(34,197,94,0.15);
  }
  .canvas-btn.btn-landing:hover {
    border-color: ${C.blue};
    box-shadow: 0 0 12px rgba(59,130,246,0.15);
  }
  .canvas-btn.btn-popup:hover {
    border-color: ${C.purple};
    box-shadow: 0 0 12px rgba(167,139,250,0.15);
  }
  .canvas-btn.btn-offer:hover {
    border-color: ${C.yellow};
    box-shadow: 0 0 12px rgba(234,179,8,0.15);
  }

  .canvas-dropdown {
    position: absolute;
    bottom: calc(100% + 10px);
    right: 0;
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 14px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 240px;
    box-shadow: 0 20px 50px rgba(0,0,0,0.6);
    z-index: 105;
    animation: dropdown-fade-in 0.15s ease-out;
  }
  @keyframes dropdown-fade-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .canvas-dropdown-title {
    font-size: 8px;
    font-weight: 850;
    color: ${C.muted};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin-bottom: 6px;
    padding: 4px 8px;
    border-bottom: 1px solid ${C.border};
  }

  .canvas-dropdown-item {
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    padding: 8px 12px;
    color: ${C.text};
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition: all 0.12s;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .canvas-dropdown-item:hover {
    background: rgba(255,255,255,0.03);
    border-color: ${C.border};
  }
  .canvas-dropdown-item.traffic-item:hover {
    background: rgba(34,197,94,0.06);
    border-color: rgba(34,197,94,0.2);
  }
  .canvas-dropdown-item.landing-item:hover {
    background: rgba(59,130,246,0.06);
    border-color: rgba(59,130,246,0.2);
  }
`;

function useGlobalCSS(css) {
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = css;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
}

/* ─── UI helpers ──────────────────────────────────────────────────────────── */
const Label = ({ children, color = C.muted }) => (
  <span style={{ fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:800, color, fontFamily:'inherit' }}>{children}</span>
);
const Badge = ({ children, bg, color }) => (
  <span style={{ fontSize:9, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', padding:'2px 7px', borderRadius:4, background:bg, color, fontFamily:'inherit' }}>{children}</span>
);
const StatPill = ({ label, value, delta, deltaColor, sub, subColor, valueColor }) => (
  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 16px', flex:1, minWidth:0 }}>
    <Label>{label}</Label>
    <div style={{ marginTop:6, display:'flex', alignItems:'baseline', gap:6 }}>
      <span style={{ fontSize:16, fontWeight:800, color:valueColor||C.text }}>{value}</span>
      {delta && <span style={{ fontSize:10, fontWeight:700, color:deltaColor }}>{delta}</span>}
    </div>
    {sub && <div style={{ fontSize:9, color:subColor||C.muted, marginTop:2, fontWeight:600 }}>{sub}</div>}
  </div>
);
const TipBar = ({ text, color = C.orange }) => (
  <div style={{ borderLeft:`3px solid ${color}`, paddingLeft:8, paddingTop:6, paddingBottom:6, paddingRight:8, fontSize:9, color:C.muted, lineHeight:1.5, fontWeight:600, background:`${color}08`, borderRadius:'0 4px 4px 0', marginTop:10 }}>{text}</div>
);
const StatsGrid = ({ items }) => (
  <div style={{ display:'grid', gridTemplateColumns:`repeat(${items.length},1fr)`, gap:5, background:`${C.surface}cc`, borderRadius:6, padding:'8px 6px', border:`1px solid ${C.border}`, marginTop:8 }}>
    {items.map(({ label, value, color }, i) => (
      <div key={i} style={{ textAlign:'center' }}>
        <div style={{ fontSize:8, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</div>
        <div style={{ fontSize:11, fontWeight:700, color:color||C.text, marginTop:2 }}>{value}</div>
      </div>
    ))}
  </div>
);
const NodeHeader = ({ left, badge, badgeBg, badgeColor }) => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
    <Label color={C.muted}>{left}</Label>
    <Badge bg={badgeBg} color={badgeColor}>{badge}</Badge>
  </div>
);
const NodeTitle = ({ children }) => (
  <div style={{ fontSize:13, fontWeight:800, color:C.text, marginTop:4, marginBottom:2 }}>{children}</div>
);
const ActionButton = ({ children, color, onClick, onMouseDown }) => (
  <button 
    onClick={onClick} 
    onMouseDown={onMouseDown || (e => e.stopPropagation())}
    style={{ width:'100%', marginTop:10, padding:'7px 10px', background:`${color}15`, border:`1px solid ${color}60`, color, borderRadius:6, fontSize:9, fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', fontFamily:'inherit', transition:'background .2s' }}
    onMouseEnter={e=>{e.currentTarget.style.background=`${color}28`}}
    onMouseLeave={e=>{e.currentTarget.style.background=`${color}15`}}>
    {children}
  </button>
);

/* ═══════════════════════════════════════════════════════════════════════════
   NODE CONTENT
═══════════════════════════════════════════════════════════════════════════ */
const NodeContent = ({ node, onFixBottleneck, onUpdateNode, onOpenAdStudio, onSelectNode, isPartnerHub, campaignData }) => {
  const t = node.type;

  /* ── Standard nodes ── */
  if (t === 'traffic') {
    const DEFAULT_CPC_BY_SOURCE = {
      googleads: 1.20, bingads: 0.75, facebook: 0.65, instagram: 0.70,
      tiktok: 0.55, taboola: 0.35, outbrain: 0.30, mgid: 0.25, native: 0.35,
      propeller: 0.008, richpush: 0.010, adcash: 0.012,
      email: 0.45, seo: 0.00, direct: 0.00, custom: 0.00,
    };
    let autoSrc = 'direct';
    const labelLower = (node.label || '').toLowerCase();
    const isCustomNode = labelLower.includes('custom');
    if (labelLower.includes('google')) autoSrc = 'googleads';
    else if (labelLower.includes('bing')) autoSrc = 'bingads';
    else if (labelLower.includes('facebook') || labelLower.includes('meta')) autoSrc = 'facebook';
    else if (labelLower.includes('instagram')) autoSrc = 'instagram';
    else if (labelLower.includes('tiktok')) autoSrc = 'tiktok';
    else if (labelLower.includes('taboola')) autoSrc = 'taboola';
    else if (labelLower.includes('outbrain')) autoSrc = 'outbrain';
    else if (labelLower.includes('native')) autoSrc = 'native';
    else if (labelLower.includes('mgid')) autoSrc = 'mgid';
    else if (labelLower.includes('propeller')) autoSrc = 'propeller';
    else if (labelLower.includes('richpush') || labelLower.includes('rich push')) autoSrc = 'richpush';
    else if (labelLower.includes('adcash')) autoSrc = 'adcash';
    else if (labelLower.includes('push') || labelLower.includes('pop')) autoSrc = 'propeller';
    else if (labelLower.includes('email') || labelLower.includes('solo')) autoSrc = 'email';
    else if (labelLower.includes('seo') || labelLower.includes('organic')) autoSrc = 'seo';
    else if (labelLower.includes('youtube') || labelLower.includes('video')) autoSrc = 'facebook';
    else { autoSrc = node.pageContent?.customSource || 'custom'; }

    const resolvedSrc = isCustomNode ? (node.pageContent?.customSource || 'custom') : autoSrc;
    const defaultCpc = DEFAULT_CPC_BY_SOURCE[resolvedSrc] ?? 0.00;

    const sourceKey = autoSrc;
    const visits = campaignData?.sources?.[sourceKey] || campaignData?.sources?.[sourceKey.toLowerCase()] || 0;
    const conversions = campaignData?.conversionsBySource?.[sourceKey] || campaignData?.conversionsBySource?.[sourceKey.toLowerCase()] || 0;
    const conversionRate = visits > 0 ? ((conversions / visits) * 100).toFixed(1) + '%' : '0.0%';

    const cpc = node.pageContent?.cpc !== undefined ? node.pageContent.cpc : defaultCpc;
    const spend = visits * cpc;

    const revenue = campaignData?.revenueBySource?.[sourceKey] || campaignData?.revenueBySource?.[sourceKey.toLowerCase()] || 0;
    const epc = visits > 0 ? (revenue / visits).toFixed(2) : '0.00';
    const roiVal = spend > 0 ? ((revenue - spend) / spend) * 100 : 0;
    const roi = roiVal.toFixed(0);

    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(34,197,94,0.4)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="Traffic" badge="ACTIVE" badgeBg="rgba(34,197,94,0.12)" badgeColor={C.green}/>
        <NodeTitle>{node.title || 'Native Ads'}</NodeTitle>
        <StatsGrid items={[
          {label:'Visits',value:visits.toLocaleString()},
          {label:'Sales',value:conversions.toString(),color:C.orange},
          {label:'CR',value:conversionRate,color:C.green}
        ]}/>
        <StatsGrid items={[
          {label:'Spend',value:`$${spend.toFixed(1)}`},
          {label:'Revenue',value:`$${revenue.toFixed(1)}`,color:C.green},
          {label:'ROI',value:roiVal >= 0 ? `+${roi}%` : `${roi}%`,color:roiVal >= 0 ? C.green : C.red}
        ]}/>
        
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button 
            onClick={(e) => { 
              e.stopPropagation(); 
              const domain = campaignData?.isPartnerHub 
                ? 'affiliateflow.ai' 
                : ((campaignData?.customDomain && campaignData.customDomain !== 'xpgo.link') ? campaignData.customDomain : 'xpgo.link');
              
              let sourceParam = 'direct';
              const label = (node.label || '').toLowerCase();
              if (label.includes('google')) sourceParam = 'googleads';
              else if (label.includes('bing')) sourceParam = 'bingads';
              else if (label.includes('facebook')) sourceParam = 'facebook';
              else if (label.includes('tiktok')) sourceParam = 'tiktok';
              else if (label.includes('taboola')) sourceParam = 'taboola';
              else if (label.includes('outbrain')) sourceParam = 'outbrain';
              else if (label.includes('native')) sourceParam = 'native';
              else if (label.includes('email')) sourceParam = 'email';
              else if (label.includes('seo')) sourceParam = 'seo';
              else {
                sourceParam = node.pageContent?.customSource || (node.title || 'custom').toLowerCase().replace(/[^a-z0-9_-]/g, '_');
              }

              const slugPath = campaignData?.slug ? `/${campaignData.slug}` : '';
              const url = (domain !== 'xpgo.link' && domain !== 'affiliateflow.ai')
                ? `https://${domain}${slugPath}?source=${sourceParam}`
                : `https://${domain}/u/${campaignData?.username || 'user'}/${campaignData?.slug || 'slug'}?source=${sourceParam}`;
              navigator.clipboard.writeText(url);
              alert(`📋 Copied Ad-Network Safe URL for ${node.title}:\n\n${url}`);
            }}
            style={{ flex: 1, padding: '8px 4px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: C.green, borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all .15s' }}
          >
            📋 Copy Ad Link
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onOpenAdStudio?.(node.title); }}
            style={{ flex: 1, padding: '8px 4px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', color: C.indigo, borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all .15s' }}
          >
            🎨 Ad Studio
          </button>
        </div>
        <TipBar text="Ad network safe URL — no redirect detected"/>
      </div>
    );
  }
  if (t === 'adv-a') {
    const visitors = campaignData?.clicks || 0;
    const clicksOut = Math.floor(visitors * 0.38);
    const convRate = visitors > 0 ? ((clicksOut / visitors) * 100).toFixed(2) + '%' : '0.00%';
    const domain = campaignData?.customDomain && campaignData.customDomain !== 'xpgo.link' ? campaignData.customDomain : 'xpgo.link';
    const pageUrl = (domain !== 'xpgo.link' && domain !== 'affiliateflow.ai')
      ? `https://${domain}/${campaignData?.slug || 'slug'}/adv-a?source=direct`
      : `https://${domain}/u/${campaignData?.username || 'user'}/${campaignData?.slug || 'slug'}/adv-a?source=direct`;

    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(239,68,68,0.35)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="ADVERTORIAL (A)" badge="CONTROL" badgeBg="rgba(59,130,246,0.15)" badgeColor={C.blue}/>
        <NodeTitle>{node.title || 'Advertorial A (Control)'}</NodeTitle>
        <StatsGrid items={[
          {label:'Visitors',value:visitors.toLocaleString()},
          {label:'Clicks Out',value:clicksOut.toLocaleString()},
          {label:'Conv Rate',value:convRate,color:C.text},
          {label:'Variant',value:'A (Control)',color:C.muted}
        ]}/>
        <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:8 }}>
          <ActionButton color={C.red} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(pageUrl);
              alert(`📋 Copied Direct URL for Variation A:\n\n${pageUrl}`);
            }}
            style={{ padding:'6px 10px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.25)', color:C.blue, borderRadius:8, fontSize:9, fontWeight:700, cursor:'pointer', transition:'all .15s', textTransform:'uppercase', letterSpacing:'0.04em' }}
          >
            🔗 Copy Variant A Link
          </button>
        </div>
        <TipBar text="Variant A direct link & A/B rotation target" color={C.blue}/>
      </div>
    );
  }
  if (t === 'adv-b') {
    const visitors = Math.floor((campaignData?.clicks || 0) * 0.5);
    const clicksOut = Math.floor(visitors * 0.42);
    const convRate = visitors > 0 ? ((clicksOut / visitors) * 100).toFixed(2) + '%' : '0.00%';
    const domain = campaignData?.customDomain && campaignData.customDomain !== 'xpgo.link' ? campaignData.customDomain : 'xpgo.link';
    const pageUrl = (domain !== 'xpgo.link' && domain !== 'affiliateflow.ai')
      ? `https://${domain}/${campaignData?.slug || 'slug'}/adv-b?source=direct`
      : `https://${domain}/u/${campaignData?.username || 'user'}/${campaignData?.slug || 'slug'}/adv-b?source=direct`;

    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(167,139,250,0.4)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="ADVERTORIAL (B)" badge="VARIANT B" badgeBg="rgba(167,139,250,0.18)" badgeColor={C.purple}/>
        <NodeTitle>{node.title || 'Advertorial B (Challenger)'}</NodeTitle>
        <StatsGrid items={[
          {label:'Visitors',value:visitors.toLocaleString()},
          {label:'Clicks Out',value:clicksOut.toLocaleString()},
          {label:'Conv Rate',value:convRate,color:C.text},
          {label:'Variant',value:'B (Curiosity)',color:C.muted}
        ]}/>
        <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:8 }}>
          <ActionButton color={C.purple} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(pageUrl);
              alert(`📋 Copied Direct URL for Variation B:\n\n${pageUrl}`);
            }}
            style={{ padding:'6px 10px', background:'rgba(167,139,250,0.1)', border:'1px solid rgba(167,139,250,0.25)', color:C.purple, borderRadius:8, fontSize:9, fontWeight:700, cursor:'pointer', transition:'all .15s', textTransform:'uppercase', letterSpacing:'0.04em' }}
          >
            🔗 Copy Variant B Link
          </button>
        </div>
        <TipBar text="Variant B direct link & A/B rotation target" color={C.purple}/>
      </div>
    );
  }
  if (t === 'landing' && !node.id?.startsWith('angle') && !node.data?.angleId) {
    const isPageBuilt = campaignData?.pages 
      ? Object.keys(campaignData.pages).some(p => p === node.id || p === node.data?.angleId || p === `page_${node.data?.angleId}` || p === `page_${node.id}`)
      : false;
    const visitors = campaignData?.clicks || 0;
    const clicksOut = campaignData?.optins || 0;
    const convRate = visitors > 0 ? ((clicksOut / visitors) * 100).toFixed(2) + '%' : '0.00%';
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(59,130,246,0.35)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="LANDING PAGE" badge={isPageBuilt ? "ACTIVE" : "PENDING BUILD"} badgeBg={isPageBuilt ? "rgba(59,130,246,0.18)" : "rgba(245,158,11,0.18)"} badgeColor={isPageBuilt ? C.blue : C.orange}/>
        <NodeTitle>{node.title || 'Capture (Squeeze)'}</NodeTitle>
        
        {!isPageBuilt ? (
          <div style={{ background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.2)', color:C.orange, fontSize:9, fontWeight:700, padding:'8px 10px', borderRadius:8, marginBottom:10, textAlign:'center', lineHeight:1.4 }}>
            ⚠️ Page not built yet — click Build Page to start
          </div>
        ) : (
          <StatsGrid items={[
            {label:'Visitors',value:visitors.toLocaleString()},
            {label:'Clicks Out',value:clicksOut.toLocaleString()},
            {label:'Conv Rate',value:convRate,color:C.text},
            {label:'Type',value:'Squeeze',color:C.muted}
          ]}/>
        )}

        <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:8 }}>
          {!isPageBuilt ? (
            <button 
              type="button"
              onClick={(e) => { e.stopPropagation(); node.data?.onBuildPage ? node.data.onBuildPage(node) : handleBuildPage(node); }}
              style={{ width:'100%', padding:'8px 12px', background:`linear-gradient(135deg, ${C.orange}, #ea580c)`, border:'none', color:'#fff', borderRadius:8, fontSize:10, fontWeight:800, cursor:'pointer', transition:'all .15s', textTransform:'uppercase', letterSpacing:'0.06em' }}
            >
              🛠 Build Page
            </button>
          ) : (
            <div style={{ display:'flex', gap:6 }}>
              <button 
                type="button"
                onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}
                style={{ flex:1, padding:'8px 4px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', color:C.green, borderRadius:8, fontSize:10, fontWeight:700, cursor:'pointer', transition:'all .15s' }}
              >
                ✏️ Edit Page
              </button>
              <button 
                type="button"
                onClick={(e) => { e.stopPropagation(); node.data?.onBuildPage ? node.data.onBuildPage(node) : handleBuildPage(node); }}
                style={{ padding:'8px 8px', background:'transparent', border:`1px solid ${C.border}`, color:C.muted, borderRadius:8, fontSize:10, fontWeight:700, cursor:'pointer', transition:'all .15s' }}
                title="Rebuild Page"
              >
                🔄 Rebuild
              </button>
            </div>
          )}
        </div>
        <TipBar text={node.pageContent?.mailerXpList ? "🟢 Connected to MailerXP list" : "Opt-in fields ready to capture leads"} color={node.pageContent?.mailerXpList ? C.green : C.blue}/>
      </div>
    );
  }
  if (t === 'quiz') {
    const visitors = campaignData?.clicks || 0;
    const completes = campaignData?.optins || 0;
    const completionRate = visitors > 0 ? ((completes / visitors) * 100).toFixed(2) + '%' : '0.00%';
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(236,72,153,0.45)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="QUIZ PAGE" badge="ACTIVE" badgeBg="rgba(236,72,153,0.15)" badgeColor={C.purple}/>
        <NodeTitle>{node.title || 'Interactive Quiz'}</NodeTitle>
        <StatsGrid items={[
          {label:'Visitors',value:visitors.toLocaleString()},
          {label:'Completed',value:completes.toLocaleString()},
          {label:'Comp. Rate',value:completionRate,color:C.text},
          {label:'Type',value:'Quiz',color:C.muted}
        ]}/>
        <ActionButton color={C.purple} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>
        <TipBar text="Interactive survey collecting leads" color={C.purple}/>
      </div>
    );
  }
  if (t === 'bridge') {
    const visitors = campaignData?.clicks || 0;
    const clicksOut = campaignData?.optins || 0;
    const ctr = visitors > 0 ? ((clicksOut / visitors) * 100).toFixed(2) + '%' : '0.00%';
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(6,182,212,0.45)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="BRIDGE PAGE" badge="ACTIVE" badgeBg="rgba(6,182,212,0.15)" badgeColor={C.blue}/>
        <NodeTitle>{node.title || 'Bridge Page'}</NodeTitle>
        <StatsGrid items={[
          {label:'Visitors',value:visitors.toLocaleString()},
          {label:'Clicks Out',value:clicksOut.toLocaleString()},
          {label:'CTR Out',value:ctr,color:C.text},
          {label:'Type',value:'Bridge',color:C.muted}
        ]}/>
        <ActionButton color={C.blue} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>
        <TipBar text="Warm recommendation pre-sell active" color={C.blue}/>
      </div>
    );
  }
  if (t === 'oto') {
    const visitors = campaignData?.clicks || 0;
    const acceptances = Math.floor(visitors * 0.12);
    const convRate = visitors > 0 ? ((acceptances / visitors) * 100).toFixed(2) + '%' : '0.00%';
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(244,63,94,0.45)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="OTO PAGE" badge="ACTIVE" badgeBg="rgba(244,63,94,0.18)" badgeColor={C.red}/>
        <NodeTitle>{node.title || 'One-Time Offer'}</NodeTitle>
        <StatsGrid items={[
          {label:'Visitors',value:visitors.toLocaleString()},
          {label:'Upgrades',value:acceptances.toLocaleString()},
          {label:'Take Rate',value:convRate,color:C.text},
          {label:'Outputs',value:'Yes / No',color:C.muted}
        ]}/>
        <ActionButton color={C.red} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>
        <TipBar text="Dual outputs active: Yes & No buttons" color={C.red}/>
      </div>
    );
  }
  if (t === 'offer') {
    const salesVal = campaignData?.conversions || 0;
    const clicksVal = campaignData?.optins || 0;
    const revenue = salesVal * 47.00;
    const epc = clicksVal > 0 ? '$' + (revenue / clicksVal).toFixed(2) : '$0.00';
    const rpm = clicksVal > 0 ? '$' + ((revenue / clicksVal) * 1000).toFixed(2) : '$0.00';
    const roi = campaignData?.clicks > 0 ? '142.5%' : '0.0%';
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid ${isPartnerHub ? 'rgba(167,139,250,0.45)' : 'rgba(234,179,8,0.35)'}`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="AFFILIATE OFFER" badge={isPartnerHub ? "🔒 LOCKED" : "ACTIVE"} badgeBg={isPartnerHub ? "rgba(167,139,250,0.15)" : "rgba(234,179,8,0.18)"} badgeColor={isPartnerHub ? C.purple : C.yellow}/>
        <NodeTitle>{node.campaignName || node.title || 'SoFi Invest'}</NodeTitle>
        <StatsGrid items={[
          {label:'EPC',value:epc},
          {label:'Sales',value:salesVal.toString()},
          {label:'RPM',value:rpm,color:C.text},
          {label:'ROI',value:roi,color:C.text}
        ]}/>
        <ActionButton color={isPartnerHub ? C.purple : C.yellow} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>
          {isPartnerHub ? "🔒 View Locked Offer" : "📝 Edit Details ↗"}
        </ActionButton>
        <TipBar text={isPartnerHub ? "Partner offer locked to Affiliate Flow AI" : "Tracking redirect link generated and active"} color={isPartnerHub ? C.purple : C.yellow}/>
      </div>
    );
  }
  if (t === 'capture-popup') {
    const views = campaignData?.clicks || 0;
    const subscribes = campaignData?.optins || 0;
    const optinRate = views > 0 ? ((subscribes / views) * 100).toFixed(2) + '%' : '0.00%';
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(167,139,250,0.45)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="CAPTURE POPUP" badge="ACTIVE" badgeBg="rgba(167,139,250,0.15)" badgeColor={C.purple}/>
        <NodeTitle>{node.title || 'Capture Popup'}</NodeTitle>
        <StatsGrid items={[
          {label:'Views',value:views.toLocaleString()},
          {label:'Subscribes',value:subscribes.toLocaleString()},
          {label:'Opt-in Rate',value:optinRate,color:C.text},
          {label:'Trigger',value:'Exit Intent',color:C.muted}
        ]}/>
        <ActionButton color={C.purple} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>
        <TipBar text={node.pageContent?.mailerXpList ? "🟢 Connected to MailerXP list" : "Syncing leads to MailerXP autoresponder"} color={node.pageContent?.mailerXpList ? C.green : C.purple}/>
      </div>
    );
  }

  /* ══ LISTICLE NODE ══════════════════════════════════════════════════════ */
  if (t === 'listicle') {
    const offers = node.offers || DEFAULT_OFFERS;
    const addOffer = (e) => {
      e.stopPropagation();
      const next = [...offers, { id:`o-${Date.now()}`, name:`Offer ${offers.length+1}`, epc:'$0.00', conv:'0.0%' }];
      onUpdateNode(node.id, { offers: next });
    };
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(249,115,22,0.45)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        {/* Header */}
        <NodeHeader left="LISTICLE" badge="MULTI-OFFER" badgeBg="rgba(249,115,22,0.15)" badgeColor={C.orange}/>
        <NodeTitle>{node.title || 'Best Finance Apps 2024'}</NodeTitle>

        {/* Page stats */}
        <StatsGrid items={[
          {label:'Views',value:(campaignData?.optins || 0).toLocaleString()},
          {label:'CTR',value:campaignData?.optins > 0 ? '42.3%' : '0.0%',color:C.text},
          {label:'Avg Time',value:campaignData?.optins > 0 ? '1m 24s' : '0s'}
        ]}/>

        {/* Offer rows — each has a visible port indicator on right */}
        <div style={{ marginTop:10 }}>
          {offers.map((offer, i) => {
            const oc = OFFER_COLORS[i % OFFER_COLORS.length];
            return (
              <div key={offer.id||i} style={{ borderTop:`1px solid ${C.border}`, padding:'7px 28px 7px 4px', position:'relative' }}>
                {/* Rank badge + name */}
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:8, fontWeight:900, background:oc.rank, color:oc.rc, width:16, height:16, borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{i+1}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:C.text, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', maxWidth:140 }}>{offer?.name || ''}</span>
                </div>
                {/* Sub-stats */}
                <div style={{ display:'flex', gap:8, paddingLeft:22, marginTop:3 }}>
                  <span style={{ fontSize:9, color:C.green, fontWeight:600 }}>EPC {offer.epc}</span>
                  <span style={{ fontSize:9, color:C.muted }}>·</span>
                  <span style={{ fontSize:9, color:C.muted, fontWeight:600 }}>Conv {offer.conv}</span>
                </div>
                {/* Visual port dot inside the row (decoration; real port handle rendered by NodeWrapper) */}
                <div style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', width:8, height:8, borderRadius:'50%', background:oc.rc, opacity:0.5, flexShrink:0 }}/>
              </div>
            );
          })}
        </div>

        {/* Action Button */}
        <ActionButton color={C.orange} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>

        {/* Add offer */}
        <button onClick={addOffer} onMouseDown={e => e.stopPropagation()}
          style={{ width:'100%', marginTop:8, padding:'6px 10px', background:`${C.orange}0d`, border:`1px dashed ${C.orange}50`, color:C.orange, borderRadius:6, fontSize:9, fontWeight:800, letterSpacing:'0.08em', cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}
          onMouseEnter={e=>{e.currentTarget.style.background=`${C.orange}20`}}
          onMouseLeave={e=>{e.currentTarget.style.background=`${C.orange}0d`}}>
          + Add Offer
        </button>
      </div>
    );
  }

  /* ══ VIDEO HUB NODE ═════════════════════════════════════════════════════ */
  if (t === 'video-hub') {
    const lessons = node.lessons || DEFAULT_LESSONS;
    const activeIdx = lessons.findIndex(l => l.active);
    const active = lessons[activeIdx >= 0 ? activeIdx : 0];

    const setActive = (e, idx) => {
      e.stopPropagation();
      onUpdateNode(node.id, { lessons: lessons.map((l, j) => ({ ...l, active: j === idx })) });
    };

    return (
      <div className="funnel-node lesson-active" style={{ background:C.card, border:`1.5px solid rgba(139,92,246,0.45)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="VIDEO HUB" badge="COURSE" badgeBg="rgba(139,92,246,0.15)" badgeColor={C.violet}/>
        <NodeTitle>{node.title || 'Affiliate Marketing 101'}</NodeTitle>

        {/* Player mockup */}
        <div style={{ marginTop:8, background:'#080a10', borderRadius:8, padding:'9px 10px', border:`1px solid rgba(139,92,246,0.25)`, display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:16, flexShrink:0 }}>🎬</span>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:8, color:C.violet, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.1em' }}>Now Playing</div>
            <div style={{ fontSize:10, color:C.text, fontWeight:700, marginTop:2, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', maxWidth:148 }}>
              {active?.title || 'Select a lesson'}
            </div>
            <div style={{ fontSize:8, color:C.muted, marginTop:1 }}>{active?.dur}</div>
          </div>
        </div>

        {/* Lesson list */}
        <div style={{ marginTop:8 }}>
          {lessons.map((lesson, i) => (
            <div key={lesson.id||i} className="lesson-row"
              style={{ display:'flex', alignItems:'center', gap:6, background: lesson.active ? 'rgba(139,92,246,0.1)' : 'transparent' }}
              onClick={e => setActive(e, i)}
              onMouseDown={e => e.stopPropagation()}>
              {/* Status icon */}
              <span style={{ fontSize:9, width:14, textAlign:'center', flexShrink:0, color: lesson.done ? C.green : lesson.active ? C.violet : C.muted, fontWeight:800 }}>
                {lesson.done ? '✓' : lesson.active ? '▶' : '○'}
              </span>
              {/* Number + title */}
              <span style={{ fontSize:9, fontWeight: lesson.active ? 700 : 600, color: lesson.active ? C.text : C.muted, flex:1, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                {String(i+1).padStart(2,'0')}. {lesson.title}
              </span>
              {/* Duration */}
              <span style={{ fontSize:8, color:C.muted, flexShrink:0 }}>{lesson.dur}</span>
            </div>
          ))}
        </div>

        {/* Action Button */}
        <ActionButton color={C.violet} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>📝 Edit Page Content ↗</ActionButton>

        {/* Footer stats */}
        <div style={{ marginTop:8, background:`${C.surface}cc`, borderRadius:5, padding:'5px 8px', border:`1px solid ${C.border}`, display:'flex', gap:10 }}>
          <span style={{ fontSize:8, color:C.muted, fontWeight:600 }}>Completion <span style={{ color:C.text }}>{campaignData?.optins > 0 ? '42%' : '0%'}</span></span>
          <span style={{ fontSize:8, color:C.muted, fontWeight:600 }}>Avg watch <span style={{ color:C.text }}>{campaignData?.optins > 0 ? '3m 12s' : '0s'}</span></span>
          <span style={{ fontSize:8, color:C.muted, fontWeight:600 }}>{lessons.length} lessons</span>
        </div>
      </div>
    );
  }

  if (t === 'custom-html' || node.data?.funnelType === 'Custom HTML (Paste Code)') {
    const hasHtml = !!(node.pageContent?.html);
    const versCount = Array.isArray(node.pageContent?.versions) ? node.pageContent.versions.length : 0;
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(16,185,129,0.5)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="CUSTOM HTML (OPT A)" badge={hasHtml ? "LIVE" : "DRAFT"} badgeBg="rgba(16,185,129,0.15)" badgeColor={C.green}/>
        <NodeTitle>{node.title || 'Custom HTML Page'}</NodeTitle>
        <StatsGrid items={[
          {label:'Origin',value:'Custom Dom',color:C.green},
          {label:'Noindex',value:node.pageContent?.noindex !== false ? 'ON' : 'OFF',color:C.text},
          {label:'Versions',value:`${versCount}/10`,color:C.muted}
        ]}/>
        <ActionButton color={C.green} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>💻 Paste / Edit HTML ↗</ActionButton>
        <TipBar text="Served only on custom domains ({{offer:1}})" color={C.green}/>
      </div>
    );
  }

  if (t === 'external-page' || node.data?.funnelType === 'External Page (Self-Hosted)') {
    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid rgba(245,158,11,0.5)`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left="EXTERNAL PAGE (OPT B)" badge="T.JS" badgeBg="rgba(245,158,11,0.15)" badgeColor={C.yellow}/>
        <NodeTitle>{node.title || 'External Site Page'}</NodeTitle>
        <StatsGrid items={[
          {label:'Tracker',value:'/t.js',color:C.yellow},
          {label:'CORS',value:'Verified',color:C.green},
          {label:'Events',value:'sendBeacon',color:C.muted}
        ]}/>
        <ActionButton color={C.yellow} onClick={(e) => { e.stopPropagation(); onSelectNode?.(node.id); }}>🌐 Snippet & Slot Links ↗</ActionButton>
        <TipBar text="Self-hosted page tracked via /t.js" color={C.yellow}/>
      </div>
    );
  }

  if (t === 'angle' || node.id?.startsWith('angle') || node.data?.angleId) {
    const status = node.data?.angleStatus || node.data?.status || node.status || 'queued';
    const isActive = status === 'active';
    
    // Check if page is built
    const isPageBuilt = campaignData?.pages 
      ? Object.keys(campaignData.pages).some(p => p === node.id || p === node.data?.angleId || p === `page_${node.data?.angleId}` || p === `page_${node.id}`)
      : false;

    // Seeding stats deterministically
    let hash = 0;
    const str = node.id || 'default';
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const seed = Math.abs(hash);
    const visits = 800 + (seed % 1200);
    const clicks = Math.floor(visits * (0.12 + (seed % 150) / 1000));
    const conversions = Math.floor(clicks * (0.02 + (seed % 80) / 1000));

    const borderCol = isActive ? 'rgba(34,197,94,0.45)' : 'rgba(234,179,8,0.3)';
    const badgeColor = isActive ? C.green : C.yellow;
    const badgeBg = isActive ? 'rgba(34,197,94,0.12)' : 'rgba(234,179,8,0.1)';

    return (
      <div className="funnel-node" style={{ background:C.card, border:`1.5px solid ${borderCol}`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
        <NodeHeader left={node.data?.funnelType || 'LANDING PAGE'} badge={status.toUpperCase()} badgeBg={badgeBg} badgeColor={badgeColor}/>
        <NodeTitle>{node.data?.angleName || node.title || 'Angle Page'}</NodeTitle>
        
        {!isPageBuilt ? (
          <div style={{ background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.2)', color:C.orange, fontSize:9, fontWeight:700, padding:'8px 10px', borderRadius:8, marginBottom:10, textAlign:'center', lineHeight:1.4 }}>
            ⚠️ Page not built yet — click Build Page to start
          </div>
        ) : (
          <StatsGrid items={[
            {label:'Views',value:visits.toLocaleString()},
            {label:'Clicks',value:clicks.toLocaleString()},
            {label:'Conv',value:conversions.toLocaleString(),color:C.green}
          ]}/>
        )}

        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {!isPageBuilt ? (
            <button 
              type="button"
              onClick={(e) => { e.stopPropagation(); node.data?.onBuildPage ? node.data.onBuildPage(node) : handleBuildPage(node); }}
              style={{ padding:'8px 12px', background:`linear-gradient(135deg, ${C.orange}, #ea580c)`, border:'none', color:'#fff', borderRadius:8, fontSize:10, fontWeight:800, cursor:'pointer', transition:'all .15s', textTransform:'uppercase', letterSpacing:'0.06em' }}
            >
              🛠 Build Page
            </button>
          ) : (
            <>
              <div style={{ display:'flex', gap:6 }}>
                <button 
                  type="button"
                  onClick={(e) => { e.stopPropagation(); node.data?.onEdit ? node.data.onEdit() : onSelectNode?.(node.id); }}
                  style={{ flex:1, padding:'8px 4px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', color:C.green, borderRadius:8, fontSize:10, fontWeight:700, cursor:'pointer', transition:'all .15s' }}
                >
                  ✏️ Edit Page
                </button>
                <button 
                  type="button"
                  onClick={(e) => { e.stopPropagation(); node.data?.onBuildPage ? node.data.onBuildPage(node) : handleBuildPage(node); }}
                  style={{ padding:'8px 8px', background:'transparent', border:`1px solid ${C.border}`, color:C.muted, borderRadius:8, fontSize:10, fontWeight:700, cursor:'pointer', transition:'all .15s' }}
                  title="Rebuild Page"
                >
                  🔄 Rebuild
                </button>
              </div>
              {/* Each built angle page is always live — no activation needed */}
              {(() => {
                const angleId = node.data?.angleId || node.id;
                const domain = campaignData?.customDomain && campaignData.customDomain !== 'xpgo.link'
                  ? campaignData.customDomain : 'xpgo.link';
                const pageUrl = (domain !== 'xpgo.link' && domain !== 'affiliateflow.ai')
                  ? `https://${domain}/${campaignData?.slug || 'slug'}/${angleId}`
                  : `https://${domain}/u/${campaignData?.username || 'user'}/${campaignData?.slug || 'slug'}/${angleId}`;
                return (
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    {(() => {
                      const hasAr = !!(node.pageContent?.mailerXpList || node.data?.mailerXpList || campaignData?.mailerXpList);
                      const customForm = node.pageContent?.customForm || node.data?.customForm || campaignData?.customForm;
                      const isCustom = !!(customForm?.action);
                      if (hasAr || isCustom) {
                        return (
                          <div style={{ background:'rgba(34,197,94,0.07)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:7, padding:'4px 8px', display:'flex', alignItems:'center', gap:5 }}>
                            <span style={{ fontSize:8, fontWeight:800, color:C.green }}>📬 {isCustom ? (customForm.provider || 'Custom Form') : 'MailerXP'}</span>
                            <span style={{ fontSize:7.5, color:C.green, fontWeight:600 }}>Active</span>
                          </div>
                        );
                      }
                      return (
                        <div style={{ background:'rgba(168,85,247,0.07)', border:'1px solid rgba(168,85,247,0.2)', borderRadius:7, padding:'4px 8px', display:'flex', alignItems:'center', gap:5 }}>
                          <span style={{ fontSize:8, fontWeight:700, color:'#c084fc' }}>📬 No Autoresponder</span>
                        </div>
                      );
                    })()}
                    <div style={{ background:'rgba(34,197,94,0.07)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:7, padding:'5px 8px', display:'flex', alignItems:'center', gap:5 }}>
                      <span style={{ fontSize:8, fontWeight:800, color:C.green, textTransform:'uppercase', letterSpacing:'0.08em', flexShrink:0 }}>🟢 LIVE</span>
                      <span style={{ fontSize:8, color:C.muted, wordBreak:'break-all', flex:1, lineHeight:1.3 }}>
                        {(domain !== 'xpgo.link' && domain !== 'affiliateflow.ai') ? `${domain}/${campaignData?.slug || 'slug'}/${angleId}` : `…/${campaignData?.slug || 'slug'}/${angleId}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(pageUrl + '?source=direct');
                        alert(`📋 Copied unique page URL:\n\n${pageUrl}?source=direct\n\nUse this link in your ads — it always shows THIS specific angle page.`);
                      }}
                      style={{ padding:'6px 10px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', color:C.green, borderRadius:8, fontSize:9, fontWeight:700, cursor:'pointer', transition:'all .15s', textTransform:'uppercase', letterSpacing:'0.04em' }}
                    >
                      🔗 Copy Unique Page URL
                    </button>
                  </div>
                );
              })()}
            </>
          )}
        </div>

      </div>
    );
  }

  /* Generic fallback */
  const cat = NODE_CATALOG.find(c => c.type === t) || NODE_CATALOG[3];
  return (
    <div className="funnel-node" style={{ background:C.card, border:`1.5px solid ${cat.border}`, borderRadius:12, padding:16, width:NW, userSelect:'none' }}>
      <div style={{ fontSize:11, fontWeight:800, color:C.text, marginBottom:8 }}>{cat.label.replace(/^\S+\s+/,'')}</div>
      <div style={{ fontSize:10, color:C.muted }}>New node — connect it to the funnel.</div>
    </div>
  );
};

/* ─── SVG Connections ─────────────────────────────────────────────────────── */
const ConnectionsSVG = ({ nodes, connections, connectingFrom, mousePos, onDeleteConn }) => {
  const srcPort = connectingFrom ? portPos(nodes, connectingFrom.nodeId, connectingFrom.portId) : null;
  const srcIsOutput = connectingFrom ? isOutputPort(connectingFrom.portId) : true;

  return (
    <svg style={{ position:'absolute', top:0, left:0, width:CW, height:CH, pointerEvents:'none', zIndex:5, overflow:'visible' }}>
      {/* A/B label */}
      {(() => {
        const a = nodes.find(n => n.id === 'adv-a'), b = nodes.find(n => n.id === 'adv-b');
        if (!a || !b) return null;
        return <text x={a.x + NW/2} y={Math.min(a.y, b.y) - 16} textAnchor="middle" fill={C.purple} fontSize={9} fontWeight={800} fontFamily="'JetBrains Mono',monospace" letterSpacing="0.12em">A/B SPLIT</text>;
      })()}

      {connections.map(conn => {
        const from = portPos(nodes, conn.from, conn.fromPort||'right');
        const to   = portPos(nodes, conn.to,   conn.toPort  ||'left');
        if (!from || !to) return null;
        const path = makePath(from.x, from.y, to.x, to.y);
        const mx = (from.x + to.x) / 2, my = (from.y + to.y) / 2;
        const col = conn.color || 'rgba(255,255,255,0.22)';
        return (
          <g key={conn.id} className="conn-group">
            <path d={path} fill="none" stroke={col} strokeWidth={2} strokeDasharray="5 4"/>
            <path d={path} fill="none" stroke="transparent" strokeWidth={18} style={{ cursor:'pointer', pointerEvents:'all' }} onClick={() => onDeleteConn(conn.id)}/>
            <g transform={`translate(${mx},${my})`} className="conn-x" style={{ pointerEvents:'all', cursor:'pointer' }} onClick={() => onDeleteConn(conn.id)}>
              <circle r={9} fill={C.card} stroke={`${C.red}80`} strokeWidth={1.5}/>
              <text textAnchor="middle" dominantBaseline="central" fill={C.red} fontSize={10} fontWeight={800}>✕</text>
            </g>
          </g>
        );
      })}

      {/* Preview line */}
      {srcPort && mousePos && (
        <path d={srcIsOutput
          ? makePath(srcPort.x, srcPort.y, mousePos.x, mousePos.y)
          : makePath(mousePos.x, mousePos.y, srcPort.x, srcPort.y)}
          fill="none" stroke={C.orange} strokeWidth={2} strokeDasharray="5 4" opacity={0.8}/>
      )}
    </svg>
  );
};

/* ─── Node Wrapper ────────────────────────────────────────────────────────── */
const NodeWrapper = ({ node, connectingFrom, onDragStart, onPortClick, onDuplicate, onDeleteNode, onFixBottleneck, onUpdateNode, onOpenAdStudio, onSelectNode, isPartnerHub, campaignData }) => {
  const ports = useMemo(() => getNodePorts(node), [node]);
  const isConnSrc = connectingFrom?.nodeId === node.id;
  const isConnecting = !!connectingFrom;

  return (
    <div className="node-wrapper" style={{ position:'absolute', left:node.x, top:node.y, width:NW, zIndex: isConnSrc ? 20 : 10 }}>

      {/* Action buttons — inside node top-right, always within hover zone */}
      <div className="nw-actions" style={{ position:'absolute', top:6, right:6, display:'flex', gap:4, zIndex:30 }}>
        {!(isPartnerHub && node.type === 'offer') && (
          <>
            <button title="Duplicate" onClick={e=>{e.stopPropagation();onDuplicate(node.id)}}
              style={{ background:`${C.purple}25`, border:`1px solid ${C.purple}55`, color:C.purple, width:24, height:24, borderRadius:6, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit', transition:'all .15s' }}
              onMouseEnter={e=>{e.currentTarget.style.background=`${C.purple}50`}}
              onMouseLeave={e=>{e.currentTarget.style.background=`${C.purple}25`}}>⊕</button>
            <button title="Delete node" onClick={e=>{e.stopPropagation();onDeleteNode(node.id)}}
              style={{ background:`${C.red}20`, border:`1px solid ${C.red}45`, color:C.red, width:24, height:24, borderRadius:6, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit', transition:'all .15s' }}
              onMouseEnter={e=>{e.currentTarget.style.background=`${C.red}50`}}
              onMouseLeave={e=>{e.currentTarget.style.background=`${C.red}20`}}>✕</button>
          </>
        )}
      </div>

      {/* Dynamic port handles */}
      {ports.map(port => {
        const isActive = isConnSrc && connectingFrom.portId === port.id;
        const isTarget = isConnecting && !isConnSrc;
        const isOffer  = port.id.startsWith('offer-');
        
        let customStyle = {};
        if (port.id === 'yes') {
          customStyle = { background: '#10b981', borderColor: '#047857', boxShadow: '0 0 8px rgba(16,185,129,0.4)' };
        } else if (port.id === 'no') {
          customStyle = { background: '#ef4444', borderColor: '#b91c1c', boxShadow: '0 0 8px rgba(239,68,68,0.4)' };
        }

        return (
          <div key={port.id}
            className={`port-handle ${port.side} ${isOffer ? 'port-offer' : ''} ${isActive ? 'ph-active' : ''} ${isTarget ? 'ph-target' : ''}`}
            style={{ top: port.cy - 8, ...customStyle }}
            title={port.id === 'yes' ? 'Yes (Upgrade Link)' : port.id === 'no' ? 'No (Skip Link)' : isOffer ? `Output: ${port.id}` : port.side === 'left' ? 'Input port' : 'Output port'}
            onClick={e => { e.stopPropagation(); onPortClick(e, node.id, port.id); }}
          />
        );
      })}

      {/* Drag handle */}
      <div 
        onMouseDown={e => { 
          if (e.target.closest('button, a, input, select, textarea')) return;
          e.preventDefault(); 
          e.stopPropagation(); 
          onDragStart(e, node.id); 
        }} 
        style={{ cursor:'grab' }}
      >
        <NodeContent node={node} onFixBottleneck={onFixBottleneck} onUpdateNode={onUpdateNode} onOpenAdStudio={onOpenAdStudio} onSelectNode={onSelectNode} isPartnerHub={isPartnerHub} campaignData={campaignData}/>
      </div>
    </div>
  );
};

/* ─── Add Node Panel ──────────────────────────────────────────────────────── */
const AddNodePanel = ({ onAdd, onClose }) => (
  <div style={{ position:'fixed', bottom:80, right:28, zIndex:100, background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:14, display:'flex', flexDirection:'column', gap:6, minWidth:220, boxShadow:`0 20px 60px rgba(0,0,0,0.7)` }}>
    <div style={{ fontSize:9, fontWeight:800, color:C.muted, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:4 }}>Add Node Type</div>
    {NODE_CATALOG.map(cat => (
      <button key={cat.type} onClick={() => { onAdd(cat.type); onClose(); }}
        style={{ background:'transparent', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', color:C.text, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'inherit', textAlign:'left', transition:'all .15s', position:'relative' }}
        onMouseEnter={e=>{e.currentTarget.style.background=`${C.orange}12`;e.currentTarget.style.borderColor=`${C.orange}40`}}
        onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.borderColor=C.border}}>
        {cat.label}
        {cat.isNew && <span style={{ marginLeft:8, fontSize:8, fontWeight:900, background:`${C.orange}20`, color:C.orange, padding:'1px 5px', borderRadius:3, textTransform:'uppercase', letterSpacing:'0.1em' }}>NEW</span>}
        <div style={{ fontSize:8, color:C.muted, marginTop:2, fontWeight:600 }}>{cat.desc}</div>
      </button>
    ))}
    <button onClick={onClose} style={{ marginTop:4, background:'transparent', border:'none', color:C.muted, fontSize:10, cursor:'pointer', fontFamily:'inherit', textAlign:'center' }}>Cancel</button>
  </div>
);

/* ─── Page Mockup preview ──────────────────────────────────────────────────── */
const MockupBrowser = ({ url = 'affiliateflow.ai/page', children }) => (
  <div style={{ background:'#0a0c12', borderRadius:8, overflow:'hidden', border:`1px solid ${C.border}` }}>
    <div style={{ background:'#111420', padding:'5px 10px', display:'flex', alignItems:'center', gap:6, borderBottom:`1px solid ${C.border}` }}>
      <div style={{ display:'flex', gap:4 }}>
        {['#ef4444','#eab308','#22c55e'].map(c => <div key={c} style={{ width:7, height:7, borderRadius:'50%', background:c, opacity:.65 }}/>)}
      </div>
      <div style={{ flex:1, background:'#1a1d27', borderRadius:4, padding:'2px 8px', fontSize:8, color:C.muted, textAlign:'center', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
        https://{url}
      </div>
    </div>
    <div style={{ padding:'12px 14px', minHeight:110 }}>{children}</div>
  </div>
);

const PageMockup = ({ node, pageContent = {} }) => {
  const t = node.type;
  const pc = pageContent;
  const H = ({ text, ph }) => <div style={{ fontSize:12, fontWeight:800, color:C.text, lineHeight:1.3, marginBottom:5 }}>{text || <span style={{color:C.muted,fontWeight:600}}>{ph}</span>}</div>;
  const Sub = ({ text, ph }) => <div style={{ fontSize:9, color:C.muted, lineHeight:1.5, marginBottom:7 }}>{text || ph}</div>;
  const CTA = ({ text, color = C.orange }) => <div style={{ display:'inline-block', padding:'6px 16px', background:color||C.orange, color:'#fff', borderRadius:6, fontSize:9, fontWeight:800 }}>{text || 'Click Here →'}</div>;

  if (t === 'adv-a' || t === 'adv-b') return (
    <MockupBrowser url="affiliateflow.ai/pre-sell">
      <div style={{ fontSize:7, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>Sponsored Article</div>
      <H text={pc.headline} ph="Write a compelling headline..."/>
      <Sub text={pc.subheadline} ph="Subheadline goes here"/>
      <div style={{ fontSize:8, color:C.muted, lineHeight:1.6, marginBottom:8 }}>
        {pc.body ? pc.body.slice(0,100)+(pc.body.length>100?'...':'') : 'Body copy previews here — this is where you pre-sell the visitor...'}
      </div>
      <CTA text={pc.ctaText} color={pc.ctaColor}/>
    </MockupBrowser>
  );
  if (t === 'landing') return (
    <MockupBrowser url="affiliateflow.ai/optin">
      <div style={{ textAlign:'center' }}>
        <H text={pc.headline} ph="Your Big Headline Here"/>
        <Sub text={pc.subheadline} ph="Compelling subheadline to boost conversions"/>
        <div style={{ background:'#111420', border:`1px solid ${C.border}`, borderRadius:5, padding:'6px 10px', marginBottom:7, fontSize:9, color:C.muted }}>{pc.inputPlaceholder||'Enter your email...'}</div>
        <CTA text={pc.ctaText} color={pc.ctaColor}/>
        <div style={{ fontSize:7, color:C.muted, marginTop:6 }}>🔒 100% Privacy. No Spam.</div>
      </div>
    </MockupBrowser>
  );
  if (t === 'quiz') return (
    <MockupBrowser url="affiliateflow.ai/quiz">
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:7, color:C.purple, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>Interactive Survey</div>
        <H text={pc.headline} ph="Quick Quiz: Find Out If You Qualify"/>
        <div style={{ background:'#1a1d27', border:`1px solid ${C.border}`, borderRadius:5, padding:6, marginBottom:7, fontSize:8, color:C.text, textAlign:'left' }}>
          ❓ {pc.question || 'What is your primary goal?'}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:7 }}>
          {[pc.option1 || 'Option 1', pc.option2 || 'Option 2', pc.option3 || 'Option 3'].map((opt, i) => (
            <div key={i} style={{ background:'#111420', border:`1px solid ${C.border}`, borderRadius:5, padding:'4px 8px', fontSize:7, color:C.muted, textAlign:'left' }}>
              • {opt}
            </div>
          ))}
        </div>
        <CTA text={pc.ctaText || 'Submit & See If I Qualify'} color={C.purple}/>
      </div>
    </MockupBrowser>
  );
  if (t === 'bridge') return (
    <MockupBrowser url="affiliateflow.ai/recommendation">
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:7, color:C.blue, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>Recommended Option</div>
        <H text={pc.headline} ph="Why We Recommend This Special Solution"/>
        <Sub text={pc.subheadline} ph="We analyzed over 50 different alternatives..."/>
        <div style={{ background:'#111420', border:`1px solid ${C.border}`, borderRadius:5, padding:6, marginBottom:7, fontSize:7, color:C.muted, textAlign:'left', lineHeight:'1.4' }}>
          ✔️ Verified & Clinically Tested <br/>
          ✔️ 100% Money-Back Guarantee
        </div>
        <CTA text={pc.ctaText || 'Continue to Official Offer'} color={C.blue}/>
      </div>
    </MockupBrowser>
  );
  if (t === 'oto') return (
    <MockupBrowser url="affiliateflow.ai/upgrade">
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:7, color:C.red, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>One Time Offer</div>
        <H text={pc.headline} ph="One Time Only Masterclass Upgrade"/>
        <Sub text={pc.subheadline} ph="Accelerate your progress. Upgrade your order now..."/>
        <div style={{ display:'flex', flexDirection:'column', gap:6, maxWidth:120, margin:'0 auto' }}>
          <CTA text={pc.ctaText || 'Yes, Upgrade My Order Now!'} color={C.red}/>
          <span style={{ fontSize:7, color:C.muted, cursor:'pointer', textDecoration:'underline' }}>No thanks, I'll pass</span>
        </div>
      </div>
    </MockupBrowser>
  );
  if (t === 'offer') return (
    <MockupBrowser url={pc.url||'affiliate-offer.com'}>
      <div style={{ textAlign:'center', padding:'4px 0' }}>
        <div style={{ fontSize:22, marginBottom:4 }}>💰</div>
        <H text={pc.offerName||node.campaignName} ph="Offer Name"/>
        <Sub text={pc.network} ph="Affiliate Network"/>
        {pc.url && <div style={{ fontSize:7, color:C.blue, marginBottom:8, wordBreak:'break-all' }}>{pc.url.slice(0,50)}</div>}
        <CTA text="Go to Offer →"/>
      </div>
    </MockupBrowser>
  );
  if (t === 'listicle') return (
    <MockupBrowser url="affiliateflow.ai/reviews">
      <H text={pc.headline} ph="Top 5 Best Products of 2024"/>
      <Sub text={pc.subheadline} ph="Ranked and reviewed by our experts"/>
      {(node.offers||DEFAULT_OFFERS).slice(0,3).map((o,i)=>(
        <div key={i} style={{ display:'flex', alignItems:'center', gap:5, marginBottom:4 }}>
          <span style={{ fontSize:8, fontWeight:900, color:[C.green,C.blue,C.yellow][i], width:14 }}>#{i+1}</span>
          <span style={{ fontSize:9, fontWeight:700, color:C.text }}>{o?.name || ''}</span>
          <span style={{ fontSize:7, color:C.green, marginLeft:'auto' }}>{o.epc} EPC</span>
        </div>
      ))}
    </MockupBrowser>
  );
  if (t === 'video-hub') return (
    <MockupBrowser url="affiliateflow.ai/course">
      <H text={pc.headline||node.title} ph="Course Title"/>
      <div style={{ background:'#080a10', borderRadius:6, padding:'8px 10px', display:'flex', alignItems:'center', gap:7 }}>
        <span style={{ fontSize:16 }}>🎬</span>
        <div><div style={{ fontSize:7, color:C.violet, fontWeight:800, textTransform:'uppercase' }}>Now Playing</div>
          <div style={{ fontSize:9, fontWeight:700, color:C.text }}>{(node.lessons||DEFAULT_LESSONS).find(l=>l.active)?.title||'Select a lesson'}</div></div>
      </div>
    </MockupBrowser>
  );
  if (t === 'traffic') return (
    <MockupBrowser url="ad-network.com/track">
      <div style={{ textAlign:'center', paddingTop:8 }}>
        <div style={{ fontSize:22, marginBottom:6 }}>📰</div>
        <H text={pc.network} ph="Traffic Source / Network"/>
        <Sub text={pc.adType} ph="Ad type — Native, Display, Search..."/>
        {pc.trackingUrl && <div style={{ fontSize:7, color:C.blue, wordBreak:'break-all' }}>{pc.trackingUrl}</div>}
      </div>
    </MockupBrowser>
  );
  return <MockupBrowser><div style={{ textAlign:'center', color:C.muted, fontSize:10, paddingTop:16 }}>No preview for this type.</div></MockupBrowser>;
};

/* ─── AI-assisted field ─────────────────────────────────────────────────────── */
const AIField = ({ label, value, onChange, onAskAI, placeholder, multiline = false, promptHint, readOnly = false }) => {
  const [local, setLocal] = useState(value || '');
  useEffect(() => { setLocal(value || ''); }, [value]);
  const base = { width:'100%', background:readOnly ? `${C.surface}88` : C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:'7px 10px', fontSize:11, color:readOnly ? C.muted : C.text, fontFamily:'inherit', outline:'none', boxSizing:'border-box', cursor:readOnly ? 'not-allowed' : 'text' };
  const onF = e => { if (!readOnly) e.currentTarget.style.borderColor = `${C.orange}60`; };
  const onB = e => { if (!readOnly) { e.currentTarget.style.borderColor = C.border; onChange(local); } };
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
        <Label>{label}</Label>
        {!readOnly && (
          <button onClick={() => onAskAI(promptHint || `Write a ${label.toLowerCase()} for this funnel page`)}
            style={{ fontSize:8, fontWeight:800, color:C.violet, background:`${C.violet}15`, border:`1px solid ${C.violet}35`, borderRadius:4, padding:'2px 7px', cursor:'pointer', fontFamily:'inherit', transition:'background .15s', letterSpacing:'0.05em' }}
            onMouseEnter={e=>{e.currentTarget.style.background=`${C.violet}30`}}
            onMouseLeave={e=>{e.currentTarget.style.background=`${C.violet}15`}}>✨ AI Coach</button>
        )}
      </div>
      {multiline
        ? <textarea value={local} rows={3} onChange={e=>!readOnly && setLocal(e.target.value)} onFocus={onF} onBlur={onB} placeholder={placeholder} style={{ ...base, resize:'vertical', lineHeight:1.55 }} readOnly={readOnly}/>
        : <input value={local} onChange={e=>!readOnly && setLocal(e.target.value)} onFocus={onF} onBlur={onB} placeholder={placeholder} style={base} readOnly={readOnly}/>
      }
    </div>
  );
};

const PageEditorFields = ({ node, pageContent = {}, onChange, onAskAI, isPartnerHub = false }) => {
  const t = node.type;
  const f = (field, val) => onChange(field, val);
  if (t==='adv-a'||t==='adv-b') return (<>
    <AIField label="Headline" value={pageContent.headline} onChange={v=>f('headline',v)} placeholder="5 Finance Apps Making People Rich in 2024..." promptHint="Write a curiosity-hook advertorial headline for a finance pre-sell page that drives clicks"/>
    <AIField label="Subheadline" value={pageContent.subheadline} onChange={v=>f('subheadline',v)} placeholder="What Wall Street doesn't want you to know..."/>
    <AIField label="Body Copy" value={pageContent.body} onChange={v=>f('body',v)} placeholder="Write your pre-sell story here..." multiline promptHint="Write persuasive advertorial body copy that warms up a visitor for an affiliate offer"/>
    <AIField label="CTA Button Text" value={pageContent.ctaText} onChange={v=>f('ctaText',v)} placeholder="Claim Free Access →"/>
    <AIField label="Offer URL" value={pageContent.url} onChange={v=>f('url',v)} placeholder="https://..."/>
  </>);
  if (t==='landing') return (<>
    <AIField label="Headline" value={pageContent.headline} onChange={v=>f('headline',v)} placeholder="Discover the #1 Finance App of 2024..." promptHint="Write a high-converting headline for a squeeze page that captures email leads"/>
    <AIField label="Subheadline" value={pageContent.subheadline} onChange={v=>f('subheadline',v)} placeholder="Join 50,000+ people already earning more..."/>
    <AIField label="Email Placeholder" value={pageContent.inputPlaceholder} onChange={v=>f('inputPlaceholder',v)} placeholder="Enter your best email..."/>
    <AIField label="CTA Button Text" value={pageContent.ctaText} onChange={v=>f('ctaText',v)} placeholder="Get Instant Access →"/>
  </>);
  if (t==='quiz') return (<>
    <AIField label="Quiz Headline" value={pageContent.headline} onChange={v=>f('headline',v)} placeholder="Quick Quiz: Find Out If You Qualify" promptHint="Write an engaging quiz/survey headline to hook users"/>
    <AIField label="Quiz Question" value={pageContent.question} onChange={v=>f('question',v)} placeholder="What is your primary goal?"/>
    <AIField label="Option 1" value={pageContent.option1} onChange={v=>f('option1',v)} placeholder="Lose weight and burn fat"/>
    <AIField label="Option 2" value={pageContent.option2} onChange={v=>f('option2',v)} placeholder="Boost energy levels"/>
    <AIField label="Option 3" value={pageContent.option3} onChange={v=>f('option3',v)} placeholder="Build long-term health"/>
    <AIField label="CTA Button Text" value={pageContent.ctaText} onChange={v=>f('ctaText',v)} placeholder="Submit & See If I Qualify →"/>
  </>);
  if (t==='bridge') return (<>
    <AIField label="Headline" value={pageContent.headline} onChange={v=>f('headline',v)} placeholder="Why We Recommend This Special Solution" promptHint="Write a high-converting bridge page recommendation headline"/>
    <AIField label="Subheadline / Rationale" value={pageContent.subheadline} onChange={v=>f('subheadline',v)} placeholder="We analyzed over 50 different alternatives..." multiline promptHint="Write the rationale of why the product is recommended"/>
    <AIField label="CTA Button Text" value={pageContent.ctaText} onChange={v=>f('ctaText',v)} placeholder="Continue to Official Offer →"/>
  </>);
  if (t==='oto') return (<>
    <AIField label="Headline" value={pageContent.headline} onChange={v=>f('headline',v)} placeholder="One Time Only Masterclass Upgrade" promptHint="Write a high-converting upgrade headline for an OTO page"/>
    <AIField label="Subheadline" value={pageContent.subheadline} onChange={v=>f('subheadline',v)} placeholder="Accelerate your progress. Upgrade your order now..."/>
    <AIField label="Yes CTA Button Text" value={pageContent.ctaText} onChange={v=>f('ctaText',v)} placeholder="Yes, Upgrade My Order Now! →"/>
    <AIField label="No Link Text" value={pageContent.noLinkText} onChange={v=>f('noLinkText',v)} placeholder="No thanks, I'll pass on this forever"/>
  </>);
  if (t==='offer') return (<>
    {isPartnerHub && (
      <div style={{ padding:'10px 12px', background:'rgba(167,139,250,0.06)', border:'1px solid rgba(167,139,250,0.25)', borderRadius:8, marginBottom:14, fontSize:10, color:C.text, lineHeight:1.4 }}>
        <span style={{ fontSize:12, marginRight:6 }}>🔒</span>
        <strong>Partner Hub Locked Offer</strong>
        <div style={{ color:C.muted, marginTop:4 }}>This campaign is locked to the official Affiliate Flow AI JVZoo offer. The destination link cannot be deleted or changed.</div>
      </div>
    )}
    <AIField label="Offer Name" value={pageContent.offerName||node.campaignName} onChange={v=>f('offerName',v)} placeholder="Offer name..." readOnly={isPartnerHub}/>
    <AIField label="Affiliate URL" value={pageContent.url} onChange={v=>f('url',v)} placeholder="https://..." readOnly={isPartnerHub}/>
    <AIField label="Network" value={pageContent.network} onChange={v=>f('network',v)} placeholder="ClickBank, JVZoo, Digistore24..." readOnly={isPartnerHub}/>
  </>);
  if (t==='listicle') return (<>
    <AIField label="Article Headline" value={pageContent.headline} onChange={v=>f('headline',v)} placeholder="5 Best Finance Apps of 2024 (Ranked)" promptHint="Write an SEO-optimized listicle headline for a finance review article that ranks in Google"/>
    <AIField label="Subheadline" value={pageContent.subheadline} onChange={v=>f('subheadline',v)} placeholder="We tested 20 apps. Here's what actually works."/>
    <AIField label="Intro Paragraph" value={pageContent.intro} onChange={v=>f('intro',v)} placeholder="Start with a hook..." multiline promptHint="Write a compelling intro for a finance product listicle review article"/>
  </>);
  if (t==='video-hub') return (<>
    <AIField label="Course Title" value={pageContent.headline||node.title} onChange={v=>f('headline',v)} placeholder="Affiliate Marketing 101..."/>
    <AIField label="Course Description" value={pageContent.description} onChange={v=>f('description',v)} placeholder="Learn to go from $0 to $1,000/day..." multiline promptHint="Write a compelling course description that makes people want to watch all the lessons"/>
  </>);
  if (t==='traffic') return (<>
    <AIField label="Network / Source" value={pageContent.network} onChange={v=>f('network',v)} placeholder="Native Ads, Facebook, Google, TikTok..."/>
    <AIField label="Ad Type" value={pageContent.adType} onChange={v=>f('adType',v)} placeholder="Native, Display, Search, Video..."/>
    <AIField label="Custom Tracking Source Parameter (?source=)" value={pageContent.customSource} onChange={v=>f('customSource',v)} placeholder="e.g. native_ads_1..."/>
    <AIField label="Tracking URL" value={pageContent.trackingUrl} onChange={v=>f('trackingUrl',v)} placeholder="https://track.example.com/..."/>
  </>);
  if (t==='capture-popup') return (<>
    <AIField label="Popup Headline" value={pageContent.headline} onChange={v=>f('headline',v)} placeholder="Register for Affiliate Flow AI Academy" promptHint="Write an attention-grabbing registration popup headline offering a valuable training or course"/>
    <AIField label="Popup Subheadline" value={pageContent.subheadline} onChange={v=>f('subheadline',v)} placeholder="Enter your name and email to secure your spot."/>
    <AIField label="Email Placeholder" value={pageContent.inputPlaceholder} onChange={v=>f('inputPlaceholder',v)} placeholder="Enter your best email address..."/>
    <AIField label="CTA Button Text" value={pageContent.ctaText} onChange={v=>f('ctaText',v)} placeholder="Register For Free &rarr;"/>
  </>);
  return <div style={{ color:C.muted, fontSize:11 }}>Select a node to edit its page content.</div>;
};

const AskAIInput = ({ onAsk }) => {
  const [val, setVal] = useState('');
  const send = () => { if (!val.trim()) return; onAsk(val.trim()); setVal(''); };
  return (
    <div style={{ display:'flex', gap:6 }}>
      <input value={val} onChange={e=>setVal(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()}
        placeholder="Write me a headline for..."
        style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:'7px 10px', fontSize:11, color:C.text, fontFamily:'inherit', outline:'none' }}
        onFocus={e=>{e.currentTarget.style.borderColor=`${C.violet}60`}}
        onBlur={e=>{e.currentTarget.style.borderColor=C.border}}/>
      <button onClick={send}
        style={{ background:C.violet, border:'none', borderRadius:6, padding:'7px 11px', cursor:'pointer', fontSize:12, color:'#fff', transition:'opacity .15s' }}
        onMouseEnter={e=>{e.currentTarget.style.opacity='.8'}}
        onMouseLeave={e=>{e.currentTarget.style.opacity='1'}}>↑</button>
    </div>
  );
};

const PageEditorPanel = ({ nodeId, nodes, onClose, onUpdateNode, onAskAI, isPartnerHub }) => {
  const node = nodes.find(n => n.id === nodeId);
  if (!node) return null;
  const pc = node.pageContent || {};
  const cat = NODE_CATALOG.find(c => c.type === node.type);
  const update = (field, val) => onUpdateNode(node.id, { pageContent: { ...pc, [field]: val } });
  return (
    <div style={{ width:360, background:C.surface, borderLeft:`1px solid ${C.border}`, display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden' }}>
      {/* Header */}
      <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <div>
          <Label color={C.orange}>Page Editor</Label>
          <div style={{ fontSize:13, fontWeight:800, color:C.text, marginTop:2 }}>{cat?.label.replace(/^\S+\s+/,'') || node.type}</div>
        </div>
        <button onClick={onClose}
          style={{ background:'transparent', border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, padding:'4px 8px', cursor:'pointer', fontSize:11, fontFamily:'inherit', transition:'all .15s' }}
          onMouseEnter={e=>{e.currentTarget.style.color=C.text;e.currentTarget.style.borderColor=C.borderHover}}
          onMouseLeave={e=>{e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border}}>✕</button>
      </div>
      {/* Live preview */}
      <div style={{ padding:'10px 14px', borderBottom:`1px solid ${C.border}`, background:`${C.bg}88`, flexShrink:0 }}>
        <Label>Live Preview</Label>
        <div style={{ marginTop:6 }}><PageMockup node={node} pageContent={pc}/></div>
      </div>
      {/* Fields */}
      <div className="cb-scroll" style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>
        <div style={{ marginBottom:10 }}>
          <span style={{ fontSize:11, fontWeight:800, color:C.text }}>✏️ Edit Page Content</span>
          <div style={{ fontSize:9, color:C.muted, marginTop:3 }}>Changes preview in real-time above</div>
        </div>
        <PageEditorFields node={node} pageContent={pc} onChange={update} onAskAI={onAskAI} isPartnerHub={isPartnerHub}/>
      </div>
    </div>
  );
};

/* ─── Funnel Canvas ─────────────────────────────────────────────────────────── */
const FunnelCanvas = ({ 
  onFixBottleneck, 
  isNew, 
  onAskAI, 
  onOpenAdStudio, 
  isPartnerHub, 
  campaignData,
  nodes,
  setNodes,
  connections,
  setConnections,
  selectedNodeId,
  onSelectNode
}) => {
  const [connectingFrom, setConnecting] = useState(null);
  const [mousePos, setMousePos]         = useState(null);
  const [showAdd, setShowAdd]           = useState(false);
  const [activeMenu, setActiveMenu]           = useState(null); // 'traffic' | 'landing' | null
  const [dragOffset, setDragOffset]     = useState({ x: 0, y: 0 });
  const [dragNodeId, setDragNodeId]     = useState(null);
  const [isPanning, setIsPanning]       = useState(false);

  const nodesRef = useRef(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  const connRef = useRef(connections);
  useEffect(() => { connRef.current = connections; }, [connections]);

  const canvasRef = useRef(null);

  /* Escape cancels connecting */
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') { setConnecting(null); setMousePos(null); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  /* Mouse tracking for preview line */
  useEffect(() => {
    if (!connectingFrom) { setMousePos(null); return; }
    const h = e => {
      const c = canvasRef.current;
      if (!c) return;
      const r = c.getBoundingClientRect();
      setMousePos({ x: e.clientX - r.left + c.scrollLeft, y: e.clientY - r.top + c.scrollTop });
    };
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, [connectingFrom]);

  /* Drag a node */
  const handleDragStart = useCallback((e, nodeId) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) return;
    const c = canvasRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const sx = e.clientX - r.left + c.scrollLeft, sy = e.clientY - r.top + c.scrollTop;
    setDragOffset({ x: sx - node.x, y: sy - node.y });
    setDragNodeId(nodeId);
    let moved = false;
    const onMove = me => {
      moved = true;
      const cx = me.clientX - r.left + c.scrollLeft, cy = me.clientY - r.top + c.scrollTop;
      setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, x: Math.max(0, cx - (sx - node.x)), y: Math.max(0, cy - (sy - node.y)) } : n));
    };
    const onUp = () => {
      if (!moved) onSelectNode(selectedNodeId === nodeId ? null : nodeId);
      setDragNodeId(null);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  /* Click a port */
  const handlePortClick = useCallback((e, nodeId, portId) => {
    if (!connectingFrom) {
      setConnecting({ nodeId, portId });
      return;
    }
    if (connectingFrom.nodeId === nodeId) { setConnecting(null); setMousePos(null); return; }

    const srcOut = isOutputPort(connectingFrom.portId);
    const [fromNode, fromPort, toNode, toPort] = srcOut
      ? [connectingFrom.nodeId, connectingFrom.portId, nodeId, portId]
      : [nodeId, portId, connectingFrom.nodeId, connectingFrom.portId];

    const exists = connRef.current.some(c => c.from === fromNode && c.fromPort === fromPort && c.to === toNode);
    if (!exists) {
      let color = 'rgba(255,255,255,0.25)';
      if (fromPort.startsWith('offer-')) {
        const idx = parseInt(fromPort.split('-')[1]);
        color = OFFER_COLORS[idx % OFFER_COLORS.length].conn;
      }
      setConnections(prev => [...prev, { id:`c-${Date.now()}`, from:fromNode, fromPort, to:toNode, toPort, color }]);
    }
    setConnecting(null);
    setMousePos(null);
  }, [connectingFrom]);

  const handleUpdateNode = useCallback((nodeId, updates) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, ...updates } : n));
  }, []);

  const handleDuplicate = useCallback(nodeId => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) return;
    const h = getNodeHeight(node);
    setNodes(prev => [...prev, { ...node, id:`${nodeId}-${Date.now()}`, x: node.x + 40, y: node.y + h + 24 }]);
  }, []);

  const handleDeleteNode = useCallback(nodeId => {
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setConnections(prev => prev.filter(c => c.from !== nodeId && c.to !== nodeId));
  }, []);

  const handleDeleteConn = useCallback(connId => {
    setConnections(prev => prev.filter(c => c.id !== connId));
  }, []);

  const handleAddNode = useCallback((type, extra = {}) => {
    const c = canvasRef.current;
    const cx = c ? c.scrollLeft + c.clientWidth / 2 - NW / 2 : 400;
    const cy = c ? c.scrollTop  + c.clientHeight / 2 - 100 : 300;
    setNodes(prev => [...prev, { 
      id:`${type}-${Date.now()}`, 
      type, 
      x: Math.max(0, cx), 
      y: Math.max(0, cy),
      title: extra.title || '',
      label: extra.label || '',
      ...extra
    }]);
  }, []);

  const handleCanvasMouseDown = (e) => {
    // Left-click (0) or middle-click (1) only
    if (e.button !== 0 && e.button !== 1) return;
    
    // Ensure we are clicking background empty space, not a node card or interactive element
    const isInteractive = e.target.closest('.funnel-node') || e.target.closest('.port-handle') || e.target.closest('button') || e.target.closest('select') || e.target.closest('input');
    if (isInteractive) return;

    const container = canvasRef.current;
    if (!container) return;

    setIsPanning(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const scrollLeftStart = container.scrollLeft;
    const scrollTopStart = container.scrollTop;

    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      container.scrollLeft = scrollLeftStart - dx;
      container.scrollTop = scrollTopStart - dy;
    };

    const handleMouseUp = () => {
      setIsPanning(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleCanvasClick = (e) => {
    // Only select null if user clicked the actual background (not dragging to pan)
    if (connectingFrom) { setConnecting(null); setMousePos(null); }
    
    const isInteractive = e.target.closest('.funnel-node') || e.target.closest('.port-handle') || e.target.closest('button') || e.target.closest('select') || e.target.closest('input');
    if (!isInteractive) {
      onSelectNode(null);
    }
  };

  return (
    <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
      <div ref={canvasRef} className="cb-scroll"
        style={{ 
          flex:1, 
          overflow:'auto', 
          position:'relative', 
          cursor: connectingFrom ? 'crosshair' : (isPanning ? 'grabbing' : 'grab'),
          userSelect: isPanning ? 'none' : 'auto'
        }}
        onMouseDown={handleCanvasMouseDown}
        onClick={handleCanvasClick}>
        <div style={{ position:'absolute', inset:0, width:CW, height:CH, backgroundImage:`radial-gradient(${C.border} 1px, transparent 1px)`, backgroundSize:'28px 28px', pointerEvents:'none' }}/>
        <div style={{ position:'relative', width:CW, height:CH }}>
          <ConnectionsSVG nodes={nodes} connections={connections} connectingFrom={connectingFrom} mousePos={mousePos} onDeleteConn={handleDeleteConn}/>
          {nodes.map(node => (
            <NodeWrapper key={node.id} node={node} connectingFrom={connectingFrom}
              onDragStart={handleDragStart} onPortClick={handlePortClick}
              onDuplicate={handleDuplicate} onDeleteNode={handleDeleteNode}
              onFixBottleneck={onFixBottleneck} onUpdateNode={handleUpdateNode}
              onOpenAdStudio={onOpenAdStudio} onSelectNode={onSelectNode}
              isPartnerHub={isPartnerHub}
              campaignData={campaignData}/>
          ))}
          {nodes.length === 0 && (
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center', pointerEvents:'none', userSelect:'none' }}>
              <div style={{ fontSize:48, marginBottom:16, opacity:0.3 }}>🗺️</div>
              <div style={{ fontSize:15, fontWeight:800, color:C.text, opacity:0.4 }}>Your funnel starts here</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:8, lineHeight:1.7, opacity:0.5 }}>Click one of the node types at the bottom-right to start building your funnel map pipeline<br/>Then drag nodes around and click port circles to connect them</div>
            </div>
          )}
        </div>

        {/* Connecting hint */}
        {connectingFrom && (
          <div style={{ position:'fixed', top:120, left:'50%', transform:'translateX(-50%)', zIndex:200, background:`${C.orange}20`, border:`1px solid ${C.orange}50`, borderRadius:8, padding:'6px 16px', fontSize:10, fontWeight:700, color:C.orange, pointerEvents:'none' }}>
            Click any port to connect · Esc to cancel
          </div>
        )}

{/* V1.3 Canvas Menu Control Row (4 buttons) */}
        <div style={{ position:'fixed', bottom:28, right: selectedNodeId ? 396 : 28, zIndex:99, display:'flex', gap:10, transition:'right .2s' }}>
          
          {/* 1. Traffic Button & Dropdown */}
          <div style={{ position:'relative' }}>
            <button className="canvas-btn btn-traffic" 
              onClick={e => { e.stopPropagation(); setActiveMenu(v => v === 'traffic' ? null : 'traffic'); }}>
              📰 Traffic
            </button>
            {activeMenu === 'traffic' && (
              <div className="canvas-dropdown">
                <div className="canvas-dropdown-title">Select Traffic Source</div>
                {[
                  { title: 'Google Ads (Search)', label: 'Google Ads', icon: '🔍', type: 'Search' },
                  { title: 'Bing Ads (Search)', label: 'Bing Ads', icon: '🔍', type: 'Search' },
                  { title: 'Facebook Ads (Social)', label: 'Facebook Ads', icon: '👤', type: 'Social' },
                  { title: 'TikTok Ads (Social)', label: 'TikTok Ads', icon: '🎵', type: 'Social' },
                  { title: 'Native Ads (Taboola/Outbrain)', label: 'Native Ads', icon: '🗞️', type: 'Native' },
                  { title: 'Email Broadcast', label: 'Email', icon: '📬', type: 'Email' },
                  { title: 'SEO / Organic', label: 'SEO', icon: '🌐', type: 'Organic' },
                  { title: 'Custom Traffic Source', label: 'Custom', icon: '➕', type: 'Custom' }
                ].map(opt => (
                  <button key={opt.title} className="canvas-dropdown-item traffic-item"
                    onClick={() => { handleAddNode('traffic', { title: opt.title, label: opt.label, trafficType: opt.type, pageContent: { network: opt.label, adType: opt.type } }); setActiveMenu(null); }}>
                    <span style={{ fontSize:14 }}>{opt.icon}</span>
                    <div style={{ display:'flex', flexDirection:'column' }}>
                      <span style={{ fontWeight:700, color:C.text }}>{opt.label}</span>
                      <span style={{ fontSize:8, color:C.muted, marginTop:1 }}>{opt.type}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 2. Landing Page Button & Dropdown */}
          <div style={{ position:'relative' }}>
            <button className="canvas-btn btn-landing"
              onClick={e => { e.stopPropagation(); setActiveMenu(v => v === 'landing' ? null : 'landing'); }}>
              📋 Landing Page
            </button>
            {activeMenu === 'landing' && (
              <div className="canvas-dropdown">
                <div className="canvas-dropdown-title">Select Landing Page Type</div>
                {[
                  { type: 'adv-a', title: 'News Advertorial', label: 'Advertorial', icon: '📰', desc: 'Pre-sell editorial article' },
                  { type: 'listicle', title: 'Product Listicle', label: 'Listicle', icon: '🗂️', desc: 'Multi-offer review comparison' },
                  { type: 'video-hub', title: 'Video Hub', label: 'Video Hub', icon: '🎬', desc: 'Visual VSL / Lesson pages' },
                  { type: 'oto', title: 'One Time Offer (OTO)', label: 'OTO Page', icon: '🔥', desc: 'Special upgrade offer (Yes/No options)' },
                  { type: 'landing', title: 'Lead Magnet Page', label: 'Lead Magnet', icon: '🧲', desc: 'Free guide optin page' },
                  { type: 'bridge', title: 'Bridge Page', label: 'Bridge Page', icon: '🌉', desc: 'Simple product presell page' },
                  { type: 'quiz', title: 'Interactive Quiz', label: 'Quiz Page', icon: '❓', desc: 'Engagement survey/quiz page' }
                ].map(opt => (
                  <button key={opt.title} className="canvas-dropdown-item landing-item"
                    onClick={() => { handleAddNode(opt.type, { title: opt.title, label: opt.label }); setActiveMenu(null); }}>
                    <span style={{ fontSize:14 }}>{opt.icon}</span>
                    <div style={{ display:'flex', flexDirection:'column' }}>
                      <span style={{ fontWeight:700, color:C.text }}>{opt.title}</span>
                      <span style={{ fontSize:8, color:C.muted, marginTop:1 }}>{opt.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 3. Capture Popup Button */}
          <button className="canvas-btn btn-popup"
            onClick={() => { handleAddNode('capture-popup', { title: 'Capture Popup', label: 'Capture Popup' }); setActiveMenu(null); }}>
            💬 Capture Pop up
          </button>

          {/* 4. Affiliate Offer Button */}
          <button className="canvas-btn btn-offer"
            onClick={() => { handleAddNode('offer', { title: campaignData?.name || 'Affiliate Offer', label: 'Affiliate Offer' }); setActiveMenu(null); }}>
            💰 Affiliate Offer
          </button>

        </div>
      </div>

    </div>
  );
};

/* ─── AI Sidebar ──────────────────────────────────────────────────────────── */
const NinjaMsg = ({ children, delay = 0 }) => (
  <div className="msg-in" style={{ background:'#0f1219', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', animationDelay:`${delay}s`, opacity:0 }}>
    <Label color={C.orange}>Ninja AI</Label>
    <div style={{ fontSize:11, lineHeight:1.55, color:C.text, marginTop:5 }}>{children}</div>
  </div>
);
const AbStatusCard = ({ onAskNinja }) => (
  <div style={{ background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8, padding:'10px 12px', marginTop:10 }}>
    <Label color={C.purple}>A/B Status</Label>
    <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:6 }}>
      <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ fontSize:10, color:C.muted, fontWeight:600 }}>Variant A (control)</span><span style={{ fontSize:11, fontWeight:800, color:C.text }}>10.88%</span></div>
      <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ fontSize:10, color:C.muted, fontWeight:600 }}>Variant B (curiosity)</span><span style={{ fontSize:11, fontWeight:800, color:C.green }}>14.10% ↑</span></div>
      <div style={{ background:'rgba(167,139,250,0.15)', borderRadius:99, height:5, overflow:'hidden', marginTop:2 }}><div className="ab-bar-fill" style={{ height:'100%', background:C.purple, borderRadius:99 }}/></div>
      <div style={{ fontSize:9, color:C.muted, fontWeight:600 }}>58% confidence — need 95%</div>
    </div>
    <ActionButton color={C.red} onClick={onAskNinja}>⚡ Ask Ninja what to do ↗</ActionButton>
  </div>
);
const AiSidebar = ({ open, chatMessages, onSendMessage, campaignLabel }) => {
  const [inputVal, setInputVal] = useState('');
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [chatMessages]);
  const send = msg => { if (!msg.trim()) return; onSendMessage(msg.trim()); setInputVal(''); };

  if (!open) return (
    <div style={{ width:44, background:C.surface, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', alignItems:'center', paddingTop:14, flexShrink:0 }}>
      <span style={{ fontSize:20 }}>🥷</span>
    </div>
  );
  return (
    <aside style={{ width:260, background:C.surface, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 10px', borderBottom:`1px solid ${C.border}` }}>
        <Label>Active Campaign</Label>
        <div style={{ fontSize:13, fontWeight:800, color:C.text, marginTop:4 }}>{campaignLabel}</div>
      </div>
      <div className="cb-scroll" style={{ flex:1, overflowY:'auto', padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
        <NinjaMsg delay={0.1}>
          Welcome to your visual funnel builder! I am your AI Coach.
          <br /><br />
          Drag nodes onto the canvas, draw connections to map your affiliate traffic pipeline, and click any node to configure its details and write high-converting copy.
        </NinjaMsg>
        {chatMessages.map((m, i) => (
          <div key={i} className="msg-in" style={{ opacity:0 }}>
            {m.role === 'user'
              ? <div style={{ background:`${C.orange}12`, border:`1px solid ${C.orange}30`, borderRadius:8, padding:'8px 12px', fontSize:11, color:C.text, lineHeight:1.5 }}><Label color={C.orange}>You</Label><div style={{ marginTop:4 }}>{m.content}</div></div>
              : <div style={{ background:'#0f1219', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px' }}><Label color={C.orange}>Ninja AI</Label><div style={{ fontSize:11, lineHeight:1.55, color:C.text, marginTop:5, whiteSpace:'pre-line' }}>{m.content}</div></div>
            }
          </div>
        ))}
        {chatMessages.length > 0 && chatMessages[chatMessages.length-1]?.role === 'user' && (
          <div style={{ display:'flex', gap:3, padding:'4px 0' }}>
            {[0,1,2].map(i => <div key={i} className="typing-dot" style={{ width:6, height:6, borderRadius:'50%', background:C.orange }}/>)}
          </div>
        )}
        <div ref={bottomRef}/>
      </div>
      <div style={{ padding:'10px 12px', borderTop:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', gap:6 }}>
          <input value={inputVal} onChange={e => setInputVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && send(inputVal)}
            placeholder="Type a command or paste a link..."
            style={{ flex:1, background:'#0d0f14', border:`1px solid ${C.border}`, borderRadius:6, padding:'8px 10px', fontSize:11, color:C.text, fontFamily:'inherit', outline:'none' }}
            onFocus={e=>{e.currentTarget.style.borderColor=C.orange+'60'}}
            onBlur={e=>{e.currentTarget.style.borderColor=C.border}}/>
          <button onClick={() => send(inputVal)}
            style={{ background:C.orange, border:'none', borderRadius:6, padding:'8px 10px', cursor:'pointer', fontSize:13, transition:'opacity .15s' }}
            onMouseEnter={e=>{e.currentTarget.style.opacity='0.8'}}
            onMouseLeave={e=>{e.currentTarget.style.opacity='1'}}>↑</button>
        </div>
      </div>
    </aside>
  );
};

/* ─── Top Nav ─────────────────────────────────────────────────────────────── */
const TopNav = ({ onBack, isNew, campaignName, onCampaignNameChange, onSave, saving, onOpenDomainSettings, currentDomain }) => {
  const links = ['Dashboard','Partner Hub','Campaign Builder','Spy Competition','Offer Scout'];
  const hasCustomDomain = !!currentDomain && currentDomain !== 'xpgo.link';

  return (
    <nav style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'0 20px', height:52, display:'flex', alignItems:'center', flexShrink:0, zIndex:50 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginRight:32 }}>
        <span style={{ fontSize:20 }}>🥷</span>
        <span style={{ fontSize:12, fontWeight:800, color:C.orange, letterSpacing:'0.15em' }}>AFFILIATE FLOW AI</span>
      </div>

      {isNew ? (
        /* New Funnel mode: show name input prominently */
        <div style={{ flex:1, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:9, fontWeight:800, color:C.muted, letterSpacing:'0.12em', textTransform:'uppercase', flexShrink:0 }}>Funnel Name</span>
          <input
            value={campaignName}
            onChange={e => onCampaignNameChange(e.target.value)}
            placeholder="My Finance Funnel..."
            style={{ background:'transparent', border:'none', borderBottom:`2px solid ${C.orange}60`, borderRadius:0, color:C.text, fontSize:16, fontWeight:800, fontFamily:'inherit', outline:'none', padding:'2px 8px', minWidth:260, transition:'border-color .15s' }}
            onFocus={e=>{e.currentTarget.style.borderBottomColor=C.orange}}
            onBlur={e=>{e.currentTarget.style.borderBottomColor=`${C.orange}60`}}
          />
          {campaignName && <span style={{ fontSize:9, color:C.muted }}>← give it a name first</span>}
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:10, fontWeight:800, color:C.orange, background:`${C.orange}12`, border:`1px solid ${C.orange}20`, padding:'3px 8px', borderRadius:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Editing Funnel</span>
          <span style={{ fontSize:13, fontWeight:750, color:C.text }}>{campaignName || 'Untitled Funnel'}</span>
          {hasCustomDomain && (
            <span style={{ fontSize:9, fontWeight:800, color:'#818cf8', background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.3)', padding:'2px 8px', borderRadius:6, fontFamily:'monospace' }}>
              🌐 {currentDomain}
            </span>
          )}
        </div>
      )}

      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        {onBack && <button onClick={onBack} style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=C.borderHover;e.currentTarget.style.color=C.text}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.muted}}>← Dashboard</button>}
        {isNew ? (
          <button onClick={onSave} disabled={saving}
            style={{ padding:'7px 20px', borderRadius:8, border:'none', background: saving ? C.muted : `linear-gradient(135deg,${C.orange},${C.violet})`, color:'#fff', fontSize:11, fontWeight:800, cursor: saving ? 'default' : 'pointer', fontFamily:'inherit', letterSpacing:'0.08em', boxShadow:`0 4px 20px ${C.orange}40`, transition:'all .2s', opacity: saving ? 0.6 : 1 }}>
            {saving ? '⏳ Saving...' : '💾 Save Funnel'}
          </button>
        ) : (
          <>
            <button
              onClick={onOpenDomainSettings}
              style={{
                padding:'5px 12px',
                borderRadius:6,
                border: hasCustomDomain ? '1px solid rgba(99,102,241,0.4)' : `1px solid ${C.border}`,
                background: hasCustomDomain ? 'rgba(99,102,241,0.1)' : 'transparent',
                color: hasCustomDomain ? '#818cf8' : C.muted,
                fontSize:10,
                fontWeight:700,
                cursor:'pointer',
                fontFamily:'inherit',
                transition:'all .15s',
                display:'flex',
                alignItems:'center',
                gap:5
              }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.borderHover;e.currentTarget.style.color=C.text}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=hasCustomDomain ? 'rgba(99,102,241,0.4)' : C.border;e.currentTarget.style.color=hasCustomDomain ? '#818cf8' : C.muted}}
              title="Configure custom domain or subdomain (e.g. deals.mybrand.com)"
            >
              🌐 Domain Settings
            </button>
            <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:10, fontWeight:700, color:C.green }}><span className="live-dot">●</span> LIVE</span>
            <button style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.borderHover;e.currentTarget.style.color=C.text}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.muted}}>⟳ Neural Regenerate</button>
            <button style={{ padding:'6px 16px', borderRadius:6, border:'none', background:C.orange, color:'#fff', fontSize:10, fontWeight:800, cursor:'pointer', fontFamily:'inherit', letterSpacing:'0.08em', transition:'opacity .15s' }}
              onMouseEnter={e=>{e.currentTarget.style.opacity='0.88'}}
              onMouseLeave={e=>{e.currentTarget.style.opacity='1'}}>🚀 Deploy Live</button>
          </>
        )}
      </div>
    </nav>
  );
};

/* ─── Domain Settings Modal ─────────────────────────────────────────────────── */
const DomainSettingsModal = ({ isOpen, onClose, currentDomain, campaignData, onSaveDomain, userProfile, userDomains }) => {
  const domainListFromProps = (userDomains || []).map(d => typeof d === 'string' ? d : d.hostname).filter(Boolean);
  const profileDomains = Array.isArray(userProfile?.customDomains) ? userProfile.customDomains : [];
  const userCustomDomains = Array.from(new Set([...domainListFromProps, ...profileDomains]));
  const [domainInput, setDomainInput] = useState(() => currentDomain && currentDomain !== 'xpgo.link' ? currentDomain : '');
  const [mode, setMode] = useState(() => (currentDomain && currentDomain !== 'xpgo.link') ? 'custom' : 'default');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDomainInput(currentDomain && currentDomain !== 'xpgo.link' ? currentDomain : '');
      setMode((currentDomain && currentDomain !== 'xpgo.link') ? 'custom' : 'default');
    }
  }, [isOpen, currentDomain]);

  if (!isOpen) return null;

  const cleanDomain = domainInput.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  const subdomain = cleanDomain.includes('.') && cleanDomain.split('.').length > 2 ? cleanDomain.split('.')[0] : '@';

  const handleSave = async () => {
    setSaving(true);
    try {
      const targetDomain = mode === 'custom' ? cleanDomain : 'xpgo.link';
      await onSaveDomain(targetDomain);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const copyCname = () => {
    navigator.clipboard.writeText('pages.affiliateflow.ai');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:260, display:'flex', alignItems:'center', justifyContent:'center', padding:16, backdropFilter:'blur(6px)' }}>
      <div style={{ background:'#121620', border:'1px solid rgba(255,255,255,0.1)', borderRadius:20, maxWidth:520, width:'100%', padding:24, boxShadow:'0 25px 70px rgba(0,0,0,0.8)', color:'#fff', fontFamily:'inherit', display:'flex', flexDirection:'column', gap:18 }}>
        
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid rgba(255,255,255,0.08)', paddingBottom:14 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'rgba(249,115,22,0.15)', border:'1px solid rgba(249,115,22,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
              🌐
            </div>
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:'#fff' }}>Campaign Domain & Branding</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>Run your funnels under your own branded domain or subdomain</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'transparent', border:'none', color:C.muted, fontSize:18, cursor:'pointer', padding:4 }}>✕</button>
        </div>

        {/* Options Selector */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <button
            type="button"
            onClick={() => setMode('default')}
            style={{
              padding:'12px',
              borderRadius:12,
              border: mode === 'default' ? `1.5px solid ${C.orange}` : '1px solid rgba(255,255,255,0.08)',
              background: mode === 'default' ? 'rgba(249,115,22,0.1)' : '#0d1017',
              textAlign:'left',
              cursor:'pointer',
              display:'flex',
              flexDirection:'column',
              gap:4
            }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:12 }}>⚡</span>
              <span style={{ fontSize:12, fontWeight:800, color: mode === 'default' ? C.orange : '#fff' }}>Default Domain</span>
            </div>
            <span style={{ fontSize:10, color:C.muted }}>xpgo.link (Instant launch)</span>
          </button>

          <button
            type="button"
            onClick={() => setMode('custom')}
            style={{
              padding:'12px',
              borderRadius:12,
              border: mode === 'custom' ? `1.5px solid ${C.orange}` : '1px solid rgba(255,255,255,0.08)',
              background: mode === 'custom' ? 'rgba(249,115,22,0.1)' : '#0d1017',
              textAlign:'left',
              cursor:'pointer',
              display:'flex',
              flexDirection:'column',
              gap:4
            }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:12 }}>🏷️</span>
              <span style={{ fontSize:12, fontWeight:800, color: mode === 'custom' ? C.orange : '#fff' }}>Custom Domain</span>
            </div>
            <span style={{ fontSize:10, color:C.muted }}>Your domain or subdomain</span>
          </button>
        </div>

        {/* Custom Domain Input & DNS Setup */}
        {mode === 'custom' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {userCustomDomains.length > 0 && (
              <div>
                <label style={{ display:'block', fontSize:10, fontWeight:800, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>
                  Quick Select from Settings:
                </label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {userCustomDomains.map((dom, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setDomainInput(dom)}
                      style={{
                        padding:'5px 10px',
                        borderRadius:8,
                        background: cleanDomain === dom ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.06)',
                        border: cleanDomain === dom ? '1px solid #818cf8' : '1px solid rgba(255,255,255,0.1)',
                        color: cleanDomain === dom ? '#fff' : C.muted,
                        fontSize:11,
                        fontFamily:'monospace',
                        cursor:'pointer',
                        display:'flex',
                        alignItems:'center',
                        gap:6
                      }}
                    >
                      <span>🌐</span>
                      <span>{dom}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label style={{ display:'block', fontSize:10, fontWeight:800, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>
                Your Domain or Subdomain
              </label>
              <input
                type="text"
                value={domainInput}
                onChange={e => setDomainInput(e.target.value)}
                placeholder="e.g. deals.mybrand.com or mypromos.com"
                style={{ width:'100%', background:'#090b10', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'10px 14px', color:'#fff', fontSize:12, fontFamily:'monospace', outline:'none', boxSizing:'border-box' }}
              />
              <span style={{ fontSize:10, color:C.muted, marginTop:4, display:'block' }}>
                Enter any subdomain (like <strong>deals.mybrand.com</strong>) or apex domain you own.
              </span>
            </div>

            {cleanDomain && (
              <div style={{ background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.25)', borderRadius:12, padding:14, display:'flex', flexDirection:'column', gap:8 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:10, fontWeight:800, color:'#818cf8', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                    DNS Setup at your Domain Registrar
                  </span>
                  <button onClick={copyCname} style={{ background:'rgba(255,255,255,0.08)', border:'none', borderRadius:6, padding:'3px 8px', color:'#fff', fontSize:10, cursor:'pointer', fontWeight:700 }}>
                    {copied ? '✓ Copied' : 'Copy Target'}
                  </button>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1.5fr', gap:8, background:'#090b10', padding:10, borderRadius:8, fontSize:11, fontFamily:'monospace' }}>
                  <div>
                    <span style={{ display:'block', fontSize:8, color:C.muted, textTransform:'uppercase' }}>Type</span>
                    <strong style={{ color:'#f59e0b' }}>CNAME</strong>
                  </div>
                  <div>
                    <span style={{ display:'block', fontSize:8, color:C.muted, textTransform:'uppercase' }}>Host / Name</span>
                    <strong style={{ color:'#fff' }}>{subdomain}</strong>
                  </div>
                  <div>
                    <span style={{ display:'block', fontSize:8, color:C.muted, textTransform:'uppercase' }}>Value / Target</span>
                    <strong style={{ color:'#10b981' }}>pages.affiliateflow.ai</strong>
                  </div>
                </div>
                <span style={{ fontSize:10, color:'rgba(255,255,255,0.6)', lineHeight:1.4 }}>
                  Add this CNAME record in your DNS provider (GoDaddy, Namecheap, Cloudflare, etc.). SSL certificates are provisioned automatically.
                </span>
              </div>
            )}

            {cleanDomain && (
              <div style={{ background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.2)', borderRadius:10, padding:12, fontSize:11 }}>
                <div style={{ fontSize:9, fontWeight:800, color:'#10b981', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>
                  ✨ Clean Live Link Preview
                </div>
                <div style={{ fontFamily:'monospace', color:'#fff', wordBreak:'break-all' }}>
                  https://{cleanDomain}/
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer Actions */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:10, borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:14 }}>
          <button onClick={onClose} style={{ padding:'8px 16px', background:'transparent', border:'1px solid rgba(255,255,255,0.15)', borderRadius:8, color:C.muted, fontSize:11, fontWeight:700, cursor:'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (mode === 'custom' && !cleanDomain)}
            style={{ padding:'8px 20px', background: saving ? C.muted : `linear-gradient(135deg, ${C.orange}, #ea580c)`, border:'none', borderRadius:8, color:'#fff', fontSize:11, fontWeight:800, cursor: saving ? 'default' : 'pointer', opacity: (mode === 'custom' && !cleanDomain) ? 0.5 : 1, transition:'all .15s' }}
          >
            {saving ? 'Saving...' : 'Save & Update Links'}
          </button>
        </div>

      </div>
    </div>
  );
};

/* ─── P&L Bar ─────────────────────────────────────────────────────────────── */
const PLBar = () => (
  <div style={{ display:'flex', gap:10, padding:'12px 20px', background:C.surface, borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
    <StatPill label="Total Spend" value="$0.00"/>
    <StatPill label="Revenue" value="$0.00"/>
    <StatPill label="ROI" value="0.0%"/>
    <StatPill label="EPC" value="$0.00"/>
    <StatPill label="Sales" value="0"/>
  </div>
);

/* ─── Legend ──────────────────────────────────────────────────────────────── */
const Legend = () => (
  <div style={{ display:'flex', gap:14, padding:'6px 20px', borderBottom:`1px solid ${C.border}`, flexShrink:0, flexWrap:'wrap' }}>
    {[{c:C.red,l:'Bottleneck'},{c:C.green,l:'Optimal'},{c:C.blue,l:'Healthy'},{c:C.yellow,l:'Lift Req'},{c:C.purple,l:'A/B Test'},{c:C.orange,l:'Listicle'},{c:C.violet,l:'Video Hub'}].map(({c,l}) => (
      <div key={l} style={{ display:'flex', alignItems:'center', gap:5 }}>
        <div style={{ width:8, height:8, borderRadius:2, background:c, flexShrink:0 }}/>
        <span style={{ fontSize:9, color:C.muted, fontWeight:700 }}>{l}</span>
      </div>
    ))}
    <div style={{ marginLeft:'auto', fontSize:9, color:C.muted, fontWeight:600 }}>Drag · Click ports to connect · Hover line to delete · ⊕ duplicate</div>
  </div>
);

/* ─── Root ────────────────────────────────────────────────────────────────── */
export default function CampaignBuilder({ campaignData, onBack, mode = 'edit', onSaveNew, userProfile }) {
  useGlobalCSS(GLOBAL_CSS);
  const isNew = mode === 'new';
  const isPartnerHub = !!campaignData?.isPartnerHub;
  const [sidebarOpen, setSidebarOpen] = useState(!isNew);
  const [activeCanvasTab, setActiveCanvasTab] = useState('canvas'); // 'canvas' | 'ad-studio'
  const [adStudioPlatform, setAdStudioPlatform] = useState('meta');
  const [chatMessages, setChatMessages] = useState([]);
  const [newFunnelName, setNewFunnelName] = useState(() => campaignData?.name || '');
  const [saving, setSaving] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [glowField, setGlowField] = useState(null);

  const [pageBuilderAngle, setPageBuilderAngle] = useState(null);
  const [showPageBuilder, setShowPageBuilder] = useState(false);
  const [angleActivateConfirm, setAngleActivateConfirm] = useState(null);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [customDomainState, setCustomDomainState] = useState(() => campaignData?.customDomain && campaignData.customDomain !== 'xpgo.link' ? campaignData.customDomain : '');
  const [userDomains, setUserDomains] = useState([]);
  const [copiedDirectAdUrl, setCopiedDirectAdUrl] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(() => {
    const cd = campaignData?.customDomain;
    return cd && cd !== 'xpgo.link' ? cd : '';
  });

  useEffect(() => {
    let isMounted = true;
    const fetchDomains = async () => {
      if (!auth?.currentUser) return;
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch('/api/settings/domains', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (isMounted && data.domains) {
            setUserDomains(data.domains);
            // If current domain is empty, check if there is a default domain
            if (!campaignData?.customDomain || campaignData.customDomain === 'xpgo.link') {
              const def = data.domains.find(d => d.isDefault && d.status === 'active');
              if (def) {
                setSelectedDomain(def.hostname);
                if (campaignData) campaignData.customDomain = def.hostname;
                setCustomDomainState(def.hostname);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to load user domains in CampaignBuilder:', err);
      }
    };
    fetchDomains();
    return () => { isMounted = false; };
  }, [campaignData]);

  const handleDomainSelect = async (domainVal) => {
    const clean = domainVal ? domainVal.trim().toLowerCase() : '';
    setSelectedDomain(clean);
    if (campaignData) {
      campaignData.customDomain = clean;
    }
    setCustomDomainState(clean);
    setNodes(curr => [...curr]);
    if (!isNew && campaignData?.id) {
      try {
        await fetch('/api/campaigns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: campaignData.id, customDomain: clean })
        });
      } catch (e) {
        console.error('Failed to persist domain to campaign:', e);
      }
    }
  };

  const handleSaveDomain = async (newDomain) => {
    const cleanDomain = (newDomain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const finalDomain = cleanDomain && cleanDomain !== 'xpgo.link' ? cleanDomain : 'xpgo.link';
    setCustomDomainState(finalDomain === 'xpgo.link' ? '' : finalDomain);
    setSelectedDomain(finalDomain === 'xpgo.link' ? '' : finalDomain);
    if (campaignData) {
      campaignData.customDomain = finalDomain;
    }
    // Refresh nodes so URL buttons update
    setNodes(curr => [...curr]);
    await handleSave(false);
  };

  // localPages stores pages built in this session so VisualPageEditor re-renders correctly
  // (campaignData is a prop — mutating it won't trigger useMemo; proper state does)
  // Seed from any already-saved pages on mount so reload/re-visits also work.
  const [localPages, setLocalPages] = useState(() => campaignData?.pages || {});

  const handleBuildPage = useCallback((angleData) => {
    setPageBuilderAngle(angleData);
    setShowPageBuilder(true);
  }, []);

  const handleActivateAngle = useCallback((angleData) => {
    const isBuilt = campaignData?.pages 
      ? Object.keys(campaignData.pages).some(p => p === angleData.id || p === angleData.data?.angleId || p === `page_${angleData.data?.angleId}` || p === `page_${angleData.id}`)
      : false;
    if (!isBuilt) {
      setAngleActivateConfirm({
        ...angleData,
        error: 'You must build the page for this angle before activating it.'
      });
    } else {
      setAngleActivateConfirm(angleData);
    }
  }, [campaignData]);

  const confirmActivateAngle = useCallback(() => {
    if (!angleActivateConfirm || angleActivateConfirm.error) return;
    const angleId = angleActivateConfirm.data?.angleId || angleActivateConfirm.id;
    let nextNodes = [];
    setNodes(curr => {
      nextNodes = curr.map(n => {
        if (n.type !== 'angle' && n.type !== 'traffic') return n;
        if (n.data?.angleId) {
          const newStatus = n.data.angleId === angleId ? 'active' : 'queued';
          return {
            ...n,
            data: { ...n.data, angleStatus: newStatus },
          };
        }
        return n;
      });
      return nextNodes;
    });

    if (campaignData) {
      const updatedAngles = (campaignData.anglePlans || []).map(a => ({
        ...a,
        status: a.id === angleId ? 'active' : 'queued'
      }));
      campaignData.anglePlans = updatedAngles;
      setTimeout(() => {
        handleSave(false);
      }, 50);
    }
    setAngleActivateConfirm(null);
  }, [angleActivateConfirm, campaignData]);

  const handlePageBuilt = useCallback((html, coachAnalysis) => {
    setShowPageBuilder(false);
    const angleId = pageBuilderAngle?.data?.angleId || pageBuilderAngle?.id;
    if (angleId) {
      const pageId = `page_${angleId}`;
      setNodes(curr => {
        const updated = curr.map(n => {
          if (n.type === 'angle' && (n.data?.angleId === angleId || n.id === angleId)) {
            return {
              ...n,
              data: { ...n.data, isPageBuilt: true }
            };
          }
          return n;
        });
        return updated;
      });

      // Store in proper React state so VisualPageEditor's useMemo triggers on the new reference
      setLocalPages(prev => ({ ...prev, [pageId]: html }));

      if (campaignData) {
        // Also keep the in-memory campaignData updated for saves
        campaignData.pages = { ...(campaignData.pages || {}), [pageId]: html };
        const currentVariants = campaignData.pageVariants || [];
        const variantIndex = currentVariants.findIndex(v => v.name === pageId || v.id === pageId);
        let updatedVariants = [...currentVariants];
        if (variantIndex > -1) {
          updatedVariants[variantIndex] = {
            ...updatedVariants[variantIndex],
            html_content: html
          };
        } else {
          updatedVariants.push({
            id: `var_${pageId}_${Math.floor(Math.random() * 100000)}`,
            name: pageId,
            html_content: html,
            status: 'live',
            clicks: 0,
            conversions: 0
          });
        }
        campaignData.pageVariants = updatedVariants;
        setTimeout(() => {
          handleSave(false);
        }, 50);
      }
    }
  }, [pageBuilderAngle, campaignData]);

  const handleUpdateNode = useCallback((nodeId, updates) => {
    setNodes(prev => {
      const next = prev.map(n => n.id === nodeId ? { ...n, ...updates } : n);
      if (campaignData) {
        campaignData.funnelArchitecture = {
          ...(campaignData.funnelArchitecture || {}),
          nodes: next
        };
        const mailerList = updates.pageContent?.mailerXpList || updates.data?.mailerXpList;
        if (mailerList) {
          campaignData.mailerXpList = mailerList;
          campaignData.collectEmails = true;
        }
      }
      return next;
    });
  }, [campaignData]);

  const handleOpenEditor = (nodeId) => {
    if (!nodeId) return;
    setSelectedNodeId(nodeId);
    
    const targetNode = nodes.find(n => n.id === nodeId);
    const nodeName = targetNode?.title || 'this page';
    const nodeType = targetNode?.type ? targetNode.type.toUpperCase() : 'page';
    
    setSidebarOpen(true);
    setChatMessages(prev => [...prev, {
      role: 'ai',
      content: `⚡ **Visual Editor loaded for "${nodeName}"** (${nodeType}). 
      
What would you like to tweak on this page? I can:
1. ✍️ Rewrite the headline for high click-through rates.
2. 🎨 Update the subheadline or Call-To-Action (CTA) text.
3. 🖼️ Regenerate/change the hero graphic.
4. ⚙️ Customize the page copy. Just tell me what you want to change!`
    }]);
  };

  const handleCloseEditor = () => {
    setSelectedNodeId(null);
    handleSave(false);
    setChatMessages(prev => [...prev, {
      role: 'ai',
      content: `Returned to Funnel Map. Changes saved.`
    }]);
  };

  // Load saved architecture or start blank
  const startNodes = useMemo(() => {
    if (isNew) return [];
    const arch = campaignData?.funnelArchitecture;
    if (arch?.nodes?.length > 0) {
      return arch.nodes.map(node => {
        let x = node.x;
        let y = node.y;
        if (x === undefined && node.position) {
          x = node.position.x;
        }
        if (y === undefined && node.position) {
          y = node.position.y;
        }
        let type = node.type;
        if (node.id && (node.id.startsWith('angle-') || node.id.startsWith('angle'))) {
          type = 'angle';
        }
        if (type === 'custom') {
          if (node.data?.stage === 'training' || node.data?.label === 'training' || node.id.includes('training') || node.id.includes('video')) {
            type = 'video-hub';
          } else {
            type = 'landing';
          }
        }

        let enrichedData = { ...node.data };
        if (type === 'angle' || node.id?.startsWith('angle') || node.data?.angleId || type === 'landing') {
          enrichedData.onBuildPage = handleBuildPage;
          enrichedData.onActivate = handleActivateAngle;
          enrichedData.onEdit = () => handleOpenEditor(node.id);
        }

        return {
          ...node,
          x: typeof x === 'number' && !isNaN(x) ? x : 100,
          y: typeof y === 'number' && !isNaN(y) ? y : 150,
          type: type,
          title: node.title || node.data?.label || node.id || 'Node',
          data: enrichedData,
          pageContent: node.pageContent || node.data?.config || {}
        };
      });
    }
    return []; // blank canvas for new/unsaved campaigns
  }, [campaignData, isNew, handleBuildPage, handleActivateAngle]);

  const startConns = useMemo(() => {
    if (isNew) return [];
    const arch = campaignData?.funnelArchitecture;
    if (arch?.connections?.length > 0) {
      return arch.connections.map((c, idx) => ({
        id: c.id || `c-auto-${idx}`,
        from: c.from || c.source,
        fromPort: c.fromPort || 'right',
        to: c.to || c.target,
        toPort: c.toPort || 'left',
        color: c.color
      }));
    }
    if (arch?.edges?.length > 0) {
      return arch.edges.map((edge, idx) => {
        let fromPort = 'right';
        if (edge.sourceHandle) {
          fromPort = edge.sourceHandle.startsWith('source-offer-') 
            ? `offer-${parseInt(edge.sourceHandle.split('-').pop()) - 1}` 
            : 'right';
        }
        return {
          id: edge.id || `c-auto-${idx}`,
          from: edge.source,
          fromPort: fromPort,
          to: edge.target,
          toPort: 'left'
        };
      });
    }
    return [];
  }, [campaignData, isNew]);

  useEffect(() => {
    if (startNodes && startNodes.length > 0) {
      setNodes(startNodes);
    }
  }, [startNodes]);

  useEffect(() => {
    if (startConns && startConns.length > 0) {
      setConnections(startConns);
    }
  }, [startConns]);

  const campaignLabel = isNew
    ? (newFunnelName || 'New Funnel')
    : (campaignData?.name
        ? `${campaignData?.name || ''}${campaignData?.network ? ` — ${campaignData.network}` : ''}`
        : 'SoFi Invest — Funnel A');

  const handleSendMessage = text => {
    setChatMessages(prev => [...prev, { role:'user', content:text }]);
    
    // If a page editor is active, route message prompts to the visual page editor AI logic
    if (selectedNodeId) {
      setTimeout(() => {
        let reply = "";
        const query = text.toLowerCase();
        const activeNode = nodes.find(n => n.id === selectedNodeId);
        const pc = activeNode?.pageContent || {};
        
        const updateField = (field, val) => {
          const newPc = { ...pc, [field]: val };
          handleUpdateNode(selectedNodeId, { pageContent: newPc });
        };
        
        const triggerGlow = (field) => {
          setGlowField(field);
          setTimeout(() => {
            setGlowField(null);
          }, 2500);
        };

        if (query.includes('headline') || query.includes('rewrite') || query.includes('hook')) {
          reply = `I have drafted an optimized headline based on direct-response copywriting principles:
"Unlock the AI Wealth Protocol: Grab the Complete 2026 Strategy Package Free"

I've automatically updated it on your page. Look at the preview to see it live!`;
          updateField('headline', "Unlock the AI Wealth Protocol: Grab the Complete 2026 Strategy Package Free");
          triggerGlow('headline');
        } else if (query.includes('image') || query.includes('graphic') || query.includes('regenerate')) {
          const nextImg = "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?q=80&w=1000&auto=format&fit=crop";
          updateField('image', nextImg);
          reply = `I've generated a new optimized hero visual for your landing page. Check the updated preview!`;
          triggerGlow('image');
        } else if (query.includes('subheadline') || query.includes('sub')) {
          reply = `I've rewritten the subheadline to build on your headline:
"Where shall we send the Neural Strategy PDF and the full 12-minute breakdown?"`;
          updateField('subheadline', "Where shall we send the Neural Strategy PDF and the full 12-minute breakdown?");
          triggerGlow('subheadline');
        } else if (query.includes('cta') || query.includes('button') || query.includes('text')) {
          reply = `I have optimized the Call-to-Action button text to create high urgency:
"Send My Strategy →"`;
          updateField('ctaText', "Send My Strategy →");
          triggerGlow('ctaText');
        } else {
          reply = `I've analyzed your page segment. Let's make sure the copy matches your niche. 

Would you like me to:
1. ⚡ Rewrite the headline for higher click-through-rates.
2. 🎨 Generate a new hero graphic/illustration.
3. ✍️ Draft a benefits checklist to increase conversion.`;
        }
        
        setChatMessages(prev => [...prev, { role: 'ai', content: reply }]);
      }, 1200);
      return;
    }

    // Default canvas AI flow
    setTimeout(() => {
      const r = {
        "My advertorial control variant is converting at only 10.88%. Give me 3 specific headline tests I should run right now.":
          "Here are 3 headline tests ranked by expected lift:\n\n1. 'The $200/Day Investing Secret Wall Street Hates' — expect +4–6%\n2. 'Warning: Don't Open a SoFi Account Until You Read This' — expect +2–4%\n3. 'I Turned $500 into $3,200 Using This Weird Investing App' — expect +3–5%",
        "The A/B test on my advertorial is at 58% confidence. What should I do while waiting for more data?":
          "Don't touch traffic allocation — keep 50/50 split running. At 58% confidence the lift is real but not conclusive. Send ~200 more visits to Variant B then re-check. I'll alert you when we cross 95%.",
      };
      setChatMessages(prev => [...prev, { role:'ai', content: r[text] || "Got it. Analyzing your funnel data now... I'll surface actionable insights momentarily." }]);
    }, 1400);
  };

  const handleFixBottleneck = () => {
    setSidebarOpen(true);
    handleSendMessage("My advertorial control variant is converting at only 10.88%. Give me 3 specific headline tests I should run right now.");
  };

  const handleOpenAdStudio = (source) => {
    setSelectedNodeId(null);
    let platform = 'meta';
    if (source) {
      const srcLower = source.toLowerCase();
      if (srcLower.includes('google')) platform = 'google_search';
      else if (srcLower.includes('bing')) platform = 'google_search';
      else if (srcLower.includes('youtube')) platform = 'youtube';
      else if (srcLower.includes('taboola') || srcLower.includes('outbrain') || srcLower.includes('revcontent') || srcLower.includes('native') || srcLower.includes('traffic')) platform = 'native';
    }
    setAdStudioPlatform(platform);
    setActiveCanvasTab('ad-studio');
  };

  /* Save funnel to API (handles both create and update) */
  const handleSave = async (redirect = true) => {
    const name = newFunnelName.trim() || campaignData?.name || 'Untitled Funnel';
    setSaving(true);
    try {
      // Sanitize nodes and connections/edges for Firestore
      const sanitizedNodes = nodes.map(({ data, ...n }) => {
        const { onEdit, onDuplicate, onCycleStage, onRename, onChangeStage, onChangeTrafficSource, onOpenAdStudio, initialLink, onRemove, icon, onBuildPage, onActivate, ...cleanData } = data || {};
        return { ...n, data: cleanData };
      });

      const edges = connections.map(c => ({
        id: c.id,
        source: c.from,
        target: c.to,
        type: 'animated'
      }));

      const payload = {
        name,
        network: campaignData?.network || 'Custom',
        customDomain: campaignData?.customDomain || 'xpgo.link',
        funnelArchitecture: { nodes: sanitizedNodes, connections, edges },
        pages: campaignData?.pages || null,
        pageVariants: campaignData?.pageVariants || [],
        anglePlans: campaignData?.anglePlans || [],
      };
      
      if (!isNew && campaignData?.id) {
        payload.id = campaignData.id;
      }
      
      const res = await fetch('/api/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (redirect) {
        onSaveNew?.(data.campaign || data);
      }
    } catch (e) {
      console.error("Failed to save campaign:", e);
      if (redirect) {
        onSaveNew?.({ name });
      }
    } finally {
      setSaving(false);
    }
  };

  const activeSlug = campaignData?.slug || '';
  const currentUsername = campaignData?.username || userProfile?.affiliateId || 'user';
  const directAdUrl = selectedDomain
    ? `https://${selectedDomain}/${activeSlug}`
    : `https://affiliateflow.ai/u/${currentUsername}/${activeSlug}`;

  return (
    <div className="cb-root">
      <TopNav
        onBack={onBack}
        isNew={isNew}
        campaignName={newFunnelName}
        onCampaignNameChange={setNewFunnelName}
        onSave={handleSave}
        saving={saving}
        onOpenDomainSettings={() => setShowDomainModal(true)}
        currentDomain={campaignData?.customDomain}
      />
      {/* ── Publish on Domain & Direct Ad Link Bar ── */}
      {!isNew && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '8px 20px',
          background: '#0d111a',
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
          flexWrap: 'wrap',
          zIndex: 45
        }}>
          {/* Left: Publish on domain selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#33D5E6', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 6 }}>
              🌐 Publish on domain:
            </span>
            <select
              value={selectedDomain}
              onChange={(e) => handleDomainSelect(e.target.value)}
              style={{
                background: '#151a24',
                color: '#fff',
                border: '1px solid rgba(51,213,230,0.3)',
                borderRadius: 8,
                padding: '5px 12px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'monospace',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="">Default (affiliateflow.ai)</option>
              {userDomains.map(d => (
                <option key={d.id || d.hostname} value={d.hostname}>
                  {d.hostname} {d.isDefault ? '★ (Default)' : ''} {d.status === 'active' ? '✓ Active' : `(${d.status})`}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowDomainModal(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: C.muted,
                fontSize: 10,
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              title="Add or configure custom domains"
            >
              + Connect Domain
            </button>
          </div>

          {/* Right: Direct Ad Link + Copy button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, maxWidth: 660, justifyContent: 'flex-end' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: '#07090f',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: '3px 8px 3px 12px',
              gap: 8,
              flex: 1,
              overflow: 'hidden'
            }}>
              <span style={{ fontSize: 9, color: '#02B6FF', fontWeight: 800, textTransform: 'uppercase', flexShrink: 0, letterSpacing: '0.05em' }}>
                DIRECT AD URL:
              </span>
              <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={directAdUrl}>
                {directAdUrl}
              </span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(directAdUrl);
                  setCopiedDirectAdUrl(true);
                  setTimeout(() => setCopiedDirectAdUrl(false), 2000);
                }}
                style={{
                  background: copiedDirectAdUrl ? '#10b981' : 'linear-gradient(135deg, #02B6FF, #0A22EE)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '5px 12px',
                  fontSize: 10,
                  fontWeight: 800,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  transition: 'all .2s',
                  boxShadow: '0 2px 8px rgba(2,182,255,0.2)'
                }}
                title="Paste this in your ad network (direct link)"
              >
                {copiedDirectAdUrl ? '✓ Copied!' : '📋 Paste this in your ad network (direct link)'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display:'flex', flex:1, overflow:'hidden', position:'relative' }}>
        <button onClick={() => setSidebarOpen(v => !v)}
          style={{ position:'absolute', left: sidebarOpen ? 248 : 32, top:18, zIndex:60, background:C.card, border:`1px solid ${C.border}`, borderRadius:'0 6px 6px 0', width:16, height:40, cursor:'pointer', color:C.muted, fontSize:9, display:'flex', alignItems:'center', justifyContent:'center', transition:'left .2s' }}
          onMouseEnter={e=>{e.currentTarget.style.color=C.text}}
          onMouseLeave={e=>{e.currentTarget.style.color=C.muted}}>
          {sidebarOpen ? '‹' : '›'}
        </button>
        <AiSidebar open={sidebarOpen} chatMessages={chatMessages} onSendMessage={handleSendMessage} campaignLabel={campaignLabel}/>
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {!isNew && !selectedNodeId && <PLBar/>}
          {activeCanvasTab === 'ad-studio' ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', position:'relative', overflow:'hidden', background:C.bg }}>
              {/* Back to Canvas header bar */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 20px', background:C.surface, borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
                <span style={{ fontSize:11, fontWeight:800, color:C.text, textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'inherit' }}>Ad Studio &mdash; {adStudioPlatform === 'google_search' ? 'Google Search' : adStudioPlatform === 'native' ? 'Native Ads' : 'Meta Ads'}</span>
                <button onClick={() => setActiveCanvasTab('canvas')}
                  style={{ marginLeft:'auto', padding:'5px 12px', borderRadius:6, border:`1px solid ${C.border}`, background:C.card, color:C.text, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=C.borderHover}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border}}>
                  &larr; Back to Funnel Map
                </button>
              </div>
              <div style={{ flex:1, overflow:'hidden' }}>
                <AdStudioModal
                  isOpen={true}
                  isEmbedded={true}
                  onClose={() => setActiveCanvasTab('canvas')}
                  campaignData={campaignData}
                  initialPlatform={adStudioPlatform}
                />
              </div>
            </div>
          ) : selectedNodeId ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', position:'relative', overflow:'hidden', background:C.bg }}>
              {(() => {
                const selNode = nodes.find(n => n.id === selectedNodeId);
                const isCustomHtmlNode = selNode?.type === 'custom-html' || selNode?.data?.funnelType === 'Custom HTML (Paste Code)';
                const isExternalPageNode = selNode?.type === 'external-page' || selNode?.data?.funnelType === 'External Page (Self-Hosted)';
                if (isCustomHtmlNode || isExternalPageNode) {
                  return (
                    <div style={{ flex:1, overflowY:'auto', padding:24 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                        <h3 style={{ fontSize:14, fontWeight:800, color:'#fff' }}>
                          {isCustomHtmlNode ? '💻 Option A: Custom HTML Landing Page Editor' : '🌐 Option B: External Page & t.js Configuration'}
                        </h3>
                        <button
                          type="button"
                          onClick={() => handleCloseEditor()}
                          style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${C.border}`, background:C.card, color:C.text, fontSize:11, fontWeight:700, cursor:'pointer' }}
                        >
                          &larr; Back to Funnel Canvas
                        </button>
                      </div>
                      <PaidTrafficManager
                        campaigns={campaignData ? [campaignData] : []}
                        initialTab={isCustomHtmlNode ? 'custom-html' : 'external-pages'}
                        selectedCampaignId={campaignData?.id || ''}
                      />
                    </div>
                  );
                }
                return (
                  <VisualPageEditor
                    nodeId={selectedNodeId}
                    nodes={nodes}
                    onClose={() => handleCloseEditor()}
                    onUpdateNode={handleUpdateNode}
                    campaignData={campaignData}
                    localPages={localPages}
                    isPartnerHub={isPartnerHub}
                    isInline={true}
                    glowField={glowField}
                    onBuildPage={handleBuildPage}
                  />
                );
              })()}
            </div>
          ) : (
            <>
              <Legend/>
              <FunnelCanvas
                onFixBottleneck={handleFixBottleneck}
                isNew={isNew}
                onAskAI={text => { setSidebarOpen(true); handleSendMessage(text); }}
                onOpenAdStudio={handleOpenAdStudio}
                isPartnerHub={isPartnerHub}
                campaignData={campaignData}
                nodes={nodes}
                setNodes={setNodes}
                connections={connections}
                setConnections={setConnections}
                selectedNodeId={selectedNodeId}
                onSelectNode={handleOpenEditor}
              />
            </>
          )}
        </div>
      </div>
      
      {/* ── Page Builder Agent Modal Drawer ── */}
      <PageBuilderAgentModal
        isOpen={showPageBuilder}
        onClose={() => setShowPageBuilder(false)}
        campaign={campaignData}
        angle={pageBuilderAngle}
        coachResult={campaignData?.coachAnalysis || null}
        onPageBuilt={handlePageBuilt}
      />

      {/* ── Angle Activation Dialog ── */}
      {angleActivateConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:250, display:'flex', alignItems:'center', justifyCentering:'center', justifyContent:'center', padding:16, backdropFilter:'blur(4px)' }}>
          <div style={{ background:'#13161d', border:'1px solid rgba(255,255,255,0.08)', borderRadius:24, padding:24, maxWidth:380, width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.6)', display:'flex', flexDirection:'column', gap:16, fontFamily:'inherit' }}>
            {angleActivateConfirm.error ? (
              <>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:24 }}>⚠️</span>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>Build Page First</div>
                    <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>Landing page required</div>
                  </div>
                </div>
                <p style={{ fontSize:11, color:C.muted, lineHeight:1.6, margin:0 }}>
                  You must build the landing page for <strong style={{ color:C.orange }}>{angleActivateConfirm.data?.angleName || angleActivateConfirm.title || angleActivateConfirm.label}</strong> before you can activate it.
                </p>
                <div style={{ paddingTop:4 }}>
                  <button
                    type="button"
                    onClick={() => setAngleActivateConfirm(null)}
                    style={{ width:'100%', py:10, background:'#b45309', border:'none', color:'#fff', borderRadius:12, fontSize:10, fontWeight:750, textTransform:'uppercase', letterSpacing:'0.06em', cursor:'pointer', height:38 }}
                  >
                    Got It
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:24 }}>⚡</span>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>Activate This Angle?</div>
                    <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>Setting <strong style={{ color:C.orange }}>{angleActivateConfirm.data?.angleName || angleActivateConfirm.title || angleActivateConfirm.label}</strong> as Active</div>
                  </div>
                </div>
                <p style={{ fontSize:11, color:C.muted, lineHeight:1.6, margin:0 }}>
                  All other angles will remain <strong style={{ color:C.yellow }}>Queued</strong>. Only one angle can be Active at a time. The page is already built and ready to receive traffic.
                </p>
                <div style={{ display:'flex', gap:12, paddingTop:4 }}>
                  <button
                    type="button"
                    onClick={() => setAngleActivateConfirm(null)}
                    style={{ flex:1, height:38, borderRadius:12, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:10, fontWeight:750, textTransform:'uppercase', letterSpacing:'0.06em', cursor:'pointer' }}
                  >Cancel</button>
                  <button
                    type="button"
                    onClick={confirmActivateAngle}
                    style={{ flex:1, height:38, borderRadius:12, border:'none', background:'#22c55e', color:'#fff', fontSize:10, fontWeight:750, textTransform:'uppercase', letterSpacing:'0.06em', cursor:'pointer', boxShadow:'0 4px 12px rgba(34,197,94,0.2)' }}
                  >Activate</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Domain Settings Modal ── */}
      <DomainSettingsModal
        isOpen={showDomainModal}
        onClose={() => setShowDomainModal(false)}
        currentDomain={campaignData?.customDomain}
        campaignData={campaignData}
        onSaveDomain={handleSaveDomain}
        userProfile={userProfile}
        userDomains={userDomains}
      />
    </div>
  );
}
